import React, { useState, useRef, useReducer, useCallback, useEffect, useMemo, Fragment } from 'react';
import './PageEditor.css';
import { filterPalette } from './paletteRules';
import { isCtaVariant, resolveCta, CTA_VARIANT_KEYS } from './ctaConfig';
import { resolveWhatYouLearnHeading, resolveAlumniHeading } from './globalCourseContent';
import { getGlobalValue } from './globalContentStore';

// Reusable Yii2-backed components. Each takes a { page: { nid } } prop and
// fetches its own data via the existing coursepages/* endpoints. We render
// them as-is inside dynamic-component nodes so the same fetch + display
// logic is reused on both the editor canvas and the public Page.js view.
import Mentor from '../Mentor';
import SuccessStories from '../SuccessStories';
import Testimonials from '../Testimonials';
import SidebarQuickForm from '../SidebarQuickForm';

// The centre-column pieces of a course page come from one `pages/getpagedata`
// payload rather than from a per-nid endpoint, so they're keyed by the source
// page's PATH. Reusing Body and BlockRenderer verbatim means the visual page
// renders byte-identically to Page.js — including the shortcode placeholders
// (currency-converter, mobile-quick-form, mobile-mentor-block) that Body mounts
// React into, which a raw-HTML node cannot do.
import Body from '../Body';
import BlockRenderer from '../BlockRenderer';
import { configData } from '../../config';

// ============================================================
// Dynamic-component wrappers
// ============================================================
// The tree only stores { type, props: { sourceNid? } } — no fetched data.
// The wrapper resolves an effective page (sourceNid override, else the
// context page passed down from Page.js), memoizes the {nid} object so
// the wrapped component's useEffect([page]) doesn't refire on every
// editor-chrome re-render, and shows a helpful placeholder when no
// source is available.

function DynamicPlaceholder({ title, note }) {
  return (
    <div className="pe-dynamic-placeholder">
      <div className="pe-dp-title">{title}</div>
      <div className="pe-dp-note">{note}</div>
    </div>
  );
}

// Resolves which course node the dynamic component should query.
// - An explicit sourceNid always wins.
// - Otherwise we ONLY inherit contextPage.nid when it's a course; visual
//   pages live in a separate `visual_pages` table with their own id space,
//   so falling back to a visual page's nid would pass a garbage id to the
//   coursepages/* endpoints and silently return an empty result — which was
//   the "heading with no data" bug the user reported.
function useDynamicPage(sourceNid, contextPage) {
  const explicit = Number(sourceNid);
  const inheritable = contextPage && contextPage.type === 'course' && Number(contextPage.nid);
  const nid = explicit || inheritable || null;
  return useMemo(() => (nid ? { nid, type: 'course' } : null), [nid]);
}

// Copy for the "please configure me" placeholder — same shape for every
// dynamic component so the hint is consistent.
const COURSE_HINT_NOTE = (endpoint) =>
  'Set a "Source course NID" in the Inspector — the course node id whose ' +
  'data to load via ' + endpoint + '. Find NIDs in Manage Course Pages. ' +
  'Visual pages do not carry course data on their own.';

function DynamicMentor({ sourceNid, contextPage }) {
  const page = useDynamicPage(sourceNid, contextPage);
  if (!page) return <DynamicPlaceholder title="Mentor Block"
    note={COURSE_HINT_NOTE('coursepages/getmentors')} />;
  return <Mentor page={page} />;
}

function DynamicSuccessStories({ sourceNid, contextPage }) {
  const page = useDynamicPage(sourceNid, contextPage);
  if (!page) return <DynamicPlaceholder title="Success Stories"
    note={COURSE_HINT_NOTE('coursepages/getsuccessstories')} />;
  return <SuccessStories page={page} />;
}

function DynamicTestimonials({ sourceNid, contextPage }) {
  const page = useDynamicPage(sourceNid, contextPage);
  if (!page) return <DynamicPlaceholder title="Testimonials"
    note={COURSE_HINT_NOTE('coursepages/gettestimonial')} />;
  return <Testimonials page={page} />;
}

// ------------------------------------------------------------
// Source-page loader (path-keyed)
// ------------------------------------------------------------
// Course Overview / What You'll Learn / Alumni / CTA — and the quick form's
// course_name + field_application_key_name — all read from a single
// `pages/getpagedata?path=…` payload. The cache is module-level and keyed by
// path so the five consumers on one canvas share ONE request instead of five,
// and so re-renders of the editor chrome never refetch.
const SOURCE_PAGE_CACHE = new Map(); // path -> { status, data, promise }

function fetchSourcePage(path) {
  const hit = SOURCE_PAGE_CACHE.get(path);
  if (hit) return hit.promise;
  const promise = fetch(
    configData.APP_URL + 'pages/getpagedata?path=' + encodeURIComponent(path),
    { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
  )
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j && j.data) || null)
    .catch(() => null);
  SOURCE_PAGE_CACHE.set(path, { promise });
  return promise;
}

// Resolves the source page payload for a `sourcePath`. Returns
// { loading, data } so each wrapper can show a placeholder while in flight.
function useSourcePage(sourcePath) {
  const path = (sourcePath || '').trim();
  const [state, setState] = useState(() => ({ loading: !!path, data: null }));
  useEffect(() => {
    if (!path) { setState({ loading: false, data: null }); return; }
    let cancelled = false;
    setState({ loading: true, data: null });
    fetchSourcePage(path).then((data) => {
      if (!cancelled) setState({ loading: false, data: data || null });
    });
    return () => { cancelled = true; };
  }, [path]);
  return state;
}

// Copy for the "please configure me" placeholder on path-keyed components.
const PATH_HINT_NOTE =
  'Set a "Source page path" in the Inspector — the course page this block ' +
  'mirrors, e.g. "courses/athlete-management-sports-agent". Pages created ' +
  'from an existing course fill this in automatically.';

// Shared shell: resolves the source page, then hands it to `render`.
function SourceBackedBlock({ sourcePath, title, render }) {
  const { loading, data } = useSourcePage(sourcePath);
  if (!sourcePath) return <DynamicPlaceholder title={title} note={PATH_HINT_NOTE} />;
  if (loading) return <DynamicPlaceholder title={title} note="Loading source page…" />;
  if (!data) return <DynamicPlaceholder title={title} note={'Could not load "' + sourcePath + '".'} />;
  return render(data);
}

// Course Overview — the source page's body, rendered by the very same <Body>
// Page.js uses, so shortcodes and inline markup behave identically.
function DynamicCourseOverview({ sourcePath }) {
  return <SourceBackedBlock sourcePath={sourcePath} title="Course Overview"
    render={(data) => <Body page={data} />} />;
}

// The three course blocks, each delegating to the same <BlockRenderer>
// Page.js drives, so their output matches the live page exactly.
function makeDynamicBlock(blockType, title) {
  return function DynamicCourseBlock({ sourcePath }) {
    return <SourceBackedBlock sourcePath={sourcePath} title={title}
      render={(data) => <BlockRenderer block={{ block_type: blockType }} page={data} />} />;
  };
}
const DynamicWhatYouLearn = makeDynamicBlock('what_you_learn', "What You'll Learn");
const DynamicAlumni       = makeDynamicBlock('alumni', 'Alumni');
const DynamicCourseCta    = makeDynamicBlock('course_cta', 'Course CTA');

function DynamicQuickForm({ sourcePath, contextPage }) {
  // SidebarQuickForm reads course_name / field_application_key_name /
  // type / nid from the page prop for form context. When a sourcePath is set
  // (pages created from an existing course) we hand it the real course payload
  // so the heading and SKU match the source. Without one it falls back to the
  // previous behaviour — the host page, or defaults.
  const { data } = useSourcePage(sourcePath);
  return <SidebarQuickForm page={data || contextPage || {}} />;
}

// ------------------------------------------------------------
// Atomic CTA (Apply / Enroll)
// ------------------------------------------------------------
// One base atom, two design-system variants. Everything about how it looks
// and where it points comes from ctaConfig.js — nothing is Inspector-
// editable. The SKU that drives the target URL is read live from the bound
// course (field_application_key_name), resolved through the same path-keyed
// source-page cache the other course components use. When no course is bound
// yet, ctaHref falls back to the base path so the link is never broken.
function DynamicCTA({ variant, sourcePath, align, fullWidth, buttonText, contextPage, readOnly }) {
  const v = isCtaVariant(variant) ? variant : 'apply';
  const { data } = useSourcePage(sourcePath);
  const src = data || contextPage || null;
  const sku = src && (src.field_application_key_name || src.sku || '');
  const cta = resolveCta(v, sku);
  // Admin-only per-node label override. Empty (the default) preserves the
  // existing "Apply Now" / "Enroll Now" behaviour driven by the global text.
  const label = (buttonText != null && String(buttonText).trim() !== '')
    ? buttonText
    : cta.text;
  const [hover, setHover] = useState(false);
  return (
    <div className="l-button l-cta" style={{ textAlign: align || 'left' }}>
      <a
        href={cta.href}
        // In the editor (readOnly=false) navigation is suppressed so a click
        // selects the node instead. On the public/view page (readOnly=true) a
        // normal click must follow the SKU-generated href — and because there
        // is no handler, "Open link in new tab" keeps working too.
        onClick={readOnly ? undefined : (e) => e.preventDefault()}
        className={'l-cta-a l-cta--' + v + (fullWidth ? ' l-cta--full' : '')}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        // Size/shape/typography come from `.l-cta .l-cta-a` in PageEditor.css
        // (single source of truth, consistent in View + Edit + new blocks).
        // Only the per-variant colour/hover is dynamic, so it stays inline.
        style={{
          background: hover ? cta.hover : cta.bg,
          color: cta.color,
        }}
      >
        {label}
      </a>
    </div>
  );
}

// ------------------------------------------------------------
// Next Start Date — fully dynamic from the course record
// ------------------------------------------------------------
// Consumes the structured feed getpagedata now returns for the course this
// page is bound to (via sourcePath / SKU):
//   data.next_start_date  -> "August 31"
//   data.course_chats[]   -> { mentor, dayLocal, timePt, timeEt,
//                              dayGmt, timeGmt, timezoneLink }
// (built server-side from smww_classroom + smww_course_chat). There is no
// hardcoded date, mentor or schedule text — every row comes from the backend,
// and the number of mentor schedules is whatever the course has. Read-only:
// the tree stores only the source binding; nothing is editable in the
// Inspector.

// Realistic 2-mentor preview used ONLY as a local-development canvas fallback
// (see below). Never rendered in a production build.
const NSD_PREVIEW = {
  next_start_date: 'August 31',
  course_chats: [
    { mentor: 'Hank Jones', dayLocal: 'MONDAYS',    timePt: '6 pm PT', timeEt: '9 pm ET', dayGmt: 'TUESDAYS',  timeGmt: '1 am GMT' },
    { mentor: 'Dan Evans',  dayLocal: 'WEDNESDAYS', timePt: '5 pm PT', timeEt: '8 pm ET', dayGmt: 'THURSDAYS', timeGmt: '12 am GMT' },
  ],
};
// CRA inlines this at build time: 'development' under `npm start`, 'production'
// in the deployed build — so the preview fallback can never reach production.
const NSD_ALLOW_PREVIEW = process.env.NODE_ENV !== 'production';

function DynamicNextStartDate({ sourcePath, contextPage }) {
  const { loading, data } = useSourcePage(sourcePath);
  const src = data || contextPage || null;

  // The course context for this block: the node's sourcePath, or a course
  // path/id carried on the host page. Its presence means the block is bound
  // to a real course (vs. an unbound node the author still needs to point).
  const boundToCourse = !!(
    sourcePath ||
    (contextPage && (contextPage.sourcePath || contextPage.field_application_key_name || contextPage.nid))
  );

  let chats = src && Array.isArray(src.course_chats) ? src.course_chats : [];
  let startDate = (src && src.next_start_date) || '';
  const stillLoading = sourcePath && loading && !src;

  // Local-dev canvas fallback: the block is bound to a valid course but the
  // feed resolved with no schedule (e.g. the staging DB / eArena tables don't
  // resolve from a local machine). Rather than leave an empty box on the
  // editor canvas, show the realistic 2-mentor preview at full 520px parity.
  // Gated to non-production builds, so production/staging ALWAYS render the
  // strict live eArena feed and never this preview.
  const usingPreview = NSD_ALLOW_PREVIEW && boundToCourse && !stillLoading && chats.length === 0;
  if (usingPreview) {
    chats = NSD_PREVIEW.course_chats;
    startDate = startDate || NSD_PREVIEW.next_start_date;
  }

  if (stillLoading) {
    return <DynamicPlaceholder title="Next Start Date" note="Loading start date & schedule…" />;
  }
  if (!chats.length) {
    // Production with no live data, or an unbound node in any build.
    return <DynamicPlaceholder title="Next Start Date"
      note={'Loads live from the course record (smww_classroom + smww_course_chat) via ' +
            'getpagedata — nothing here is hand-edited. Bind this block to a course to populate it.'} />;
  }

  return (
    <div className="l-nsb" data-preview={usingPreview ? '1' : undefined}>
      <div className="nsb-head">
        <span className="nsb-head-txt">
          {getGlobalValue('next_start_date_label')}{" "}
          <span className="nsb-head-date">{startDate}</span>
        </span>
      </div>
      {chats.map((c, i) => (
        <Fragment key={i}>
          <div className="nsb-mentor">
            {getGlobalValue('next_start_date_chats_prefix')} <b>{c.mentor}</b>
          </div>
          <div className="nsb-days">
            <span className="nsb-day">{c.dayLocal}</span>
            <span className="nsb-t nsb-t--o">{c.timePt}</span>
            <span className="nsb-t nsb-t--o">{c.timeEt}</span>
            <span className="nsb-days-sp" />
            <span className="nsb-day">{c.dayGmt}</span>
            <span className="nsb-t nsb-t--b">{c.timeGmt}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

// ------------------------------------------------------------
// Mentor carousel — structured cards from the course's mentor feed
// ------------------------------------------------------------
// Iterates the selected course's mentors (coursepages/getmentors, keyed by
// the course nid — the rich feed with photo + bio; course_chats only carries
// chat names). Each card is a left thumbnail + right name / role / bio /
// LEARN MORE, laid out in a horizontally scrollable row. In a non-production
// build, when the feed is empty/pending, a realistic 2-mentor preview keeps
// the editor canvas populated; production always renders the live feed.
const MENTOR_CACHE = new Map(); // nid -> Promise<mentor[]>

function fetchMentors(nid) {
  if (MENTOR_CACHE.has(nid)) return MENTOR_CACHE.get(nid);
  const promise = fetch(configData.APP_URL + 'coursepages/getmentors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nid }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j && j.data && j.data.msg === 'Success' && Array.isArray(j.data.result) ? j.data.result : []))
    .catch(() => []);
  MENTOR_CACHE.set(nid, promise);
  return promise;
}

function useMentors(nid) {
  const [state, setState] = useState(() => ({ loading: !!nid, mentors: [] }));
  useEffect(() => {
    if (!nid) { setState({ loading: false, mentors: [] }); return; }
    let cancelled = false;
    setState({ loading: true, mentors: [] });
    fetchMentors(nid).then((list) => { if (!cancelled) setState({ loading: false, mentors: list || [] }); });
    return () => { cancelled = true; };
  }, [nid]);
  return state;
}

// Dev-only preview (mirrors the reference cards). Never used in production.
const MENTOR_PREVIEW = [
  { name: 'Dan Evans', role: 'Former GM, LA Dodgers',
    bio: 'Former Los Angeles Dodgers General Manager and SABR President; began as a Toronto Blue Jays scout. Four decades inside pro baseball operations.', img: '', link: '#' },
  { name: 'Dr. Lynn Lashbrook', role: 'SMWW President & Founder',
    bio: 'Founder of Sports Management Worldwide, certified agent, and former NCAA Athletic Director. Has mentored thousands into sports careers.', img: '', link: '#' },
];
const MENTOR_ALLOW_PREVIEW = process.env.NODE_ENV !== 'production';

// getmentors returns relative image paths ("/basic/web/pictures/…"); make them
// absolute against the SMWW origin so they load from the editor too.
const mentorImg = (img) => {
  if (!img) return '';
  return /^https?:\/\//.test(img) ? img : ((configData.RSMWW_URL || '') + img);
};

function DynamicMentorCarousel({ sourceNid, contextPage }) {
  const explicit = Number(sourceNid);
  const inheritable = contextPage && contextPage.type === 'course' && Number(contextPage.nid);
  const nid = explicit || inheritable || null;
  const { loading, mentors } = useMentors(nid);

  let list = mentors;
  const usingPreview = MENTOR_ALLOW_PREVIEW && !loading && (!list || list.length === 0);
  if (usingPreview) list = MENTOR_PREVIEW;

  // Continuous, seamless horizontal auto-scroll. The cards are rendered twice
  // (see below); once the row has scrolled past the first copy we subtract its
  // width to snap back to the visually identical start — so the loop has no
  // seam and no visible jump. Pauses on hover and honors reduced-motion.
  const rowRef = useRef(null);
  const count = list ? list.length : 0;
  useEffect(() => {
    const row = rowRef.current;
    if (!row || count === 0) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    let paused = false;
    let raf;
    const SPEED = 0.5; // px per frame (~30px/s at 60fps)
    const tick = () => {
      const half = row.scrollWidth / 2; // width of one copy of the list
      if (!paused && half > 0 && row.clientWidth < half) {
        row.scrollLeft += SPEED;
        if (row.scrollLeft >= half) row.scrollLeft -= half;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    row.addEventListener('mouseenter', pause);
    row.addEventListener('mouseleave', resume);
    return () => {
      cancelAnimationFrame(raf);
      row.removeEventListener('mouseenter', pause);
      row.removeEventListener('mouseleave', resume);
    };
  }, [count]);

  if (nid && loading && (!list || !list.length)) {
    return <DynamicPlaceholder title="Mentors" note="Loading mentors…" />;
  }
  if (!list || !list.length) {
    return <DynamicPlaceholder title="Mentors"
      note="No mentors found for this course. Loads live from coursepages/getmentors — set a Source course NID or bind the page to a course." />;
  }

  const renderCard = (m, key) => {
    const src = mentorImg(m.img);
    return (
      <div className="l-mentors-card" key={key}>
        {src
          ? <img className="l-mentors-photo" src={src} alt={m.name || ''} />
          : <div className="l-mentors-photo l-mentors-photo--empty" aria-hidden="true" />}
        <div className="l-mentors-body">
          <div className="l-mentors-name">{m.name}</div>
          {m.role ? <div className="l-mentors-role">{m.role}</div> : null}
          <div className="l-mentors-bio" dangerouslySetInnerHTML={{ __html: m.bio || '' }} />
          <a className="l-mentors-more" href={m.link || '#'} onClick={(e) => e.preventDefault()}>LEARN MORE →</a>
        </div>
      </div>
    );
  };

  return (
    <div className="l-mentors" data-preview={usingPreview ? '1' : undefined}>
      <h2 className="l-mentors-title">Your Mentors Work in the Game</h2>
      <p className="l-mentors-sub">
        Every session is taught live by people who have scouted, signed, and run pro baseball —
        not lecturers reading a textbook.
      </p>
      <div className="l-mentors-row" ref={rowRef}>
        {list.map((m, i) => renderCard(m, i))}
        {/* Duplicate copy — kept aria-hidden — enables the seamless loop. */}
        {list.map((m, i) => renderCard(m, `dup-${i}`))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Success Stories / Alumni carousel — "Where Our Alumni Work Now"
// ------------------------------------------------------------
// Same feed/mechanism as the mentor carousel, but vertical-photo cards
// (photo on top, name, role/org) in a horizontally scrollable row, with an
// editable title + subtitle built into the component so every page — new and
// already-saved — renders the redesign. Live feed = coursepages/
// getsuccessstories (name, title(HTML role), img); dev-only preview keeps the
// editor populated when the feed is empty/pending.
const SS_CACHE = new Map(); // nid -> Promise<story[]>

function fetchSuccessStories(nid) {
  if (SS_CACHE.has(nid)) return SS_CACHE.get(nid);
  const promise = fetch(configData.APP_URL + 'coursepages/getsuccessstories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nid }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j && j.data && j.data.msg === 'Success' && Array.isArray(j.data.result) ? j.data.result : []))
    .catch(() => []);
  SS_CACHE.set(nid, promise);
  return promise;
}

function useSuccessStories(nid) {
  const [state, setState] = useState(() => ({ loading: !!nid, items: [] }));
  useEffect(() => {
    if (!nid) { setState({ loading: false, items: [] }); return; }
    let cancelled = false;
    setState({ loading: true, items: [] });
    fetchSuccessStories(nid).then((list) => { if (!cancelled) setState({ loading: false, items: list || [] }); });
    return () => { cancelled = true; };
  }, [nid]);
  return state;
}

// Dev-only preview (mirrors the reference cards). Never used in production.
const ALUMNI_PREVIEW = [
  { name: 'Scott Powers',       title: 'Assistant GM, Houston Astros', img: '' },
  { name: 'Naomi Hill',         title: 'Amateur Scouting Associate, New York Mets', img: '' },
  { name: 'Jake Tillinghast',   title: 'Pro Scout, Baltimore Orioles', img: '' },
  { name: 'Ricardo Williamson', title: 'General Manager, Toros de Tijuana', img: '' },
];
const ALUMNI_ALLOW_PREVIEW = process.env.NODE_ENV !== 'production';
// Subtitle default, used when the node carries no subtitle prop at all
// (template nodes built as plain literals, and pages saved before this prop
// existed — e.g. …visual-19). An explicit blank ('') means the author cleared
// it and is respected. The H2 heading is NOT a prop — it is a single global
// value (see resolveAlumniHeading), so it is never stored per course.
const ALUMNI_DEFAULT_SUB =
  'SMWW graduates are scouting, analyzing, and running baseball operations ' +
  'across the majors, the minors, and international leagues.';

function DynamicSuccessStoriesCarousel({ sourceNid, contextPage, subtitle }) {
  const explicit = Number(sourceNid);
  const inheritable = contextPage && contextPage.type === 'course' && Number(contextPage.nid);
  const nid = explicit || inheritable || null;
  const { loading, items } = useSuccessStories(nid);

  // Heading — single GLOBAL value shared across every course. It comes from
  // Global Course Content (`resolveAlumniHeading`, backend `alumni_heading`),
  // NOT from a per-course prop, so it can't be edited on the individual course
  // page and one admin change updates every page at once. Never stored in the
  // course page JSON.
  const heading = resolveAlumniHeading();

  // Subtitle stays per-course. undefined/null => default; '' => author cleared it.
  const sub = subtitle == null ? ALUMNI_DEFAULT_SUB : String(subtitle);

  let list = items;
  const usingPreview = ALUMNI_ALLOW_PREVIEW && !loading && (!list || list.length === 0);
  if (usingPreview) list = ALUMNI_PREVIEW;

  // Continuous, seamless horizontal auto-scroll — must run in both the editor
  // canvas AND the read-only/View render. The View column is wider than the
  // editor canvas, so a single duplicate of the narrow alumni cards can fit
  // without overflowing, which previously left nothing to scroll in View. So
  // we fill the row with enough copies that the track always overflows by at
  // least one copy at the current width, and reset scroll at one-copy width —
  // making the wrap invisible at any width. Pauses on hover; honors
  // reduced-motion.
  const rowRef = useRef(null);
  const posRef = useRef(0); // float scroll accumulator — see auto-scroll note below
  const count = list ? list.length : 0;
  const [copies, setCopies] = useState(2);

  // Seamless-loop geometry, measured from real card positions so it is exact
  // and identical in every browser: offsetLeft is an integer and stable, and it
  // never depends on sub-pixel scroll rounding. One "card unit" = card width +
  // gap (the arrow step); one "copy unit" = the distance after which the
  // repeated list looks identical (the auto-scroll reset point).
  const cardUnit = () => {
    const row = rowRef.current;
    if (!row || row.children.length < 2) return 0;
    return row.children[1].offsetLeft - row.children[0].offsetLeft;
  };
  const copyUnit = () => {
    const row = rowRef.current;
    if (!row || count === 0) return 0;
    if (row.children.length > count) {
      return row.children[count].offsetLeft - row.children[0].offsetLeft;
    }
    return row.scrollWidth / copies;
  };

  // Pick the copy count so one copy's width is always a valid seamless reset
  // unit for the current viewport. Converges in one step (card widths are
  // fixed) and re-measures on data change and window resize.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || count === 0) return undefined;
    const measure = () => {
      const oneCopy = row.scrollWidth / copies;
      if (oneCopy <= 0) return;
      const needed = Math.max(2, Math.ceil(row.clientWidth / oneCopy) + 1);
      if (needed !== copies) setCopies(needed);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [count, copies]);

  // Continuous, seamless horizontal auto-scroll — runs in both the editor
  // canvas AND the read-only/View render. Cross-browser reliability note:
  // Firefox exposes `scrollLeft` as an INTEGER, so the old `scrollLeft += 0.5`
  // read-modify-write truncated the 0.5 back to 0 every frame and the row never
  // moved (it only ever worked in Chrome, which keeps sub-pixel scrollLeft). We
  // now keep the position in a JS float accumulator (posRef) and only ever
  // WRITE it to scrollLeft — never read scrollLeft back — so movement
  // accumulates identically in Chrome, Firefox, Safari and Edge. Pauses on
  // hover; honors reduced-motion. The Prev/Next arrows share this same
  // accumulator, so auto-scroll resumes cleanly from wherever an arrow left off.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || count === 0) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    let paused = false;
    let raf;
    const SPEED = 0.5; // px per frame (~30px/s at 60fps)
    const tick = () => {
      // Width of one copy of the list — measured from real card positions
      // (integer, stable, browser-independent), same rule as copyUnit().
      const kids = row.children;
      const unit = kids.length > count
        ? kids[count].offsetLeft - kids[0].offsetLeft
        : row.scrollWidth / copies;
      if (!paused && unit > 0 && row.scrollWidth - row.clientWidth > 1) {
        let pos = posRef.current + SPEED;
        if (pos >= unit) pos -= unit;
        posRef.current = pos;
        row.scrollLeft = pos; // write only; never read scrollLeft back (Firefox-safe)
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    row.addEventListener('mouseenter', pause);
    row.addEventListener('mouseleave', resume);
    return () => {
      cancelAnimationFrame(raf);
      row.removeEventListener('mouseenter', pause);
      row.removeEventListener('mouseleave', resume);
    };
  }, [count, copies]);

  // Prev/Next arrows — step exactly one card and keep the shared accumulator in
  // sync so the auto-scroll continues seamlessly afterwards. Plain scrollLeft
  // writes (no browser-specific APIs), so they behave the same everywhere.
  const stepBy = (dir) => {
    const row = rowRef.current;
    if (!row) return;
    const card = cardUnit();
    const unit = copyUnit();
    if (card <= 0 || unit <= 0) return;
    let pos = posRef.current + dir * card;
    pos = ((pos % unit) + unit) % unit; // wrap into [0, unit)
    posRef.current = pos;
    row.scrollLeft = pos;
  };
  const handlePrev = () => stepBy(-1);
  const handleNext = () => stepBy(1);

  const renderCard = (a, key, dup) => {
    const src = mentorImg(a.img);
    return (
      <div className={dup ? 'l-alumni-card l-alumni-card--dup' : 'l-alumni-card'}
           key={key} aria-hidden={dup ? 'true' : undefined}>
        {src
          ? <img className="l-alumni-photo" src={src} alt={a.name || ''} />
          : <div className="l-alumni-photo l-alumni-photo--empty" aria-hidden="true" />}
        <div className="l-alumni-body">
          <div className="l-alumni-name">{a.name}</div>
          <div className="l-alumni-role" dangerouslySetInnerHTML={{ __html: a.title || '' }} />
        </div>
      </div>
    );
  };

  const header = (
    <>
      {heading ? <h2 className="l-alumni-title">{heading}</h2> : null}
      {sub ? <p className="l-alumni-sub">{sub}</p> : null}
    </>
  );

  if (nid && loading && (!list || !list.length)) {
    return <div className="l-alumni">{header}<DynamicPlaceholder title="Alumni" note="Loading alumni…" /></div>;
  }
  if (!list || !list.length) {
    return <div className="l-alumni">{header}
      <DynamicPlaceholder title="Alumni"
        note="No alumni found for this course. Loads live from coursepages/getsuccessstories." />
    </div>;
  }

  return (
    <div className="l-alumni" data-preview={usingPreview ? '1' : undefined}>
      {heading ? <h2 className="l-alumni-title">{heading}</h2> : null}
      <div className="l-alumni-top">
        {sub ? <p className="l-alumni-sub">{sub}</p> : null}
        <div className="l-alumni-arrows">
          <button type="button" className="l-alumni-arrow" aria-label="Previous" onClick={handlePrev}>&#8249;</button>
          <button type="button" className="l-alumni-arrow" aria-label="Next" onClick={handleNext}>&#8250;</button>
          <span className="l-alumni-line" aria-hidden="true" />
        </div>
      </div>
      <div className="l-alumni-row" ref={rowRef}>
        {/* Repeated copies (count chosen to always overflow) enable the seamless
            loop; copies past the first are aria-hidden and hidden on mobile
            (where cards stack instead of scrolling). */}
        {Array.from({ length: copies }).map((_, c) =>
          list.map((a, i) => renderCard(a, `${c}-${i}`, c > 0))
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Content-binding resolver — shared by HeadingView & TextView
// ------------------------------------------------------------
// One rule, applied the same way in the editor AND the published view:
//
//   • authored `text` is non-empty  -> show `text` (an author override)
//   • authored `text` is blank + a `bind` field is set -> show the LIVE value
//     from the bound course's getpagedata
//   • neither -> ''
//
// So a freshly-bound node (blank text) renders the live course value right on
// the editor canvas; the moment the author types — in the Inspector Text
// field or inline — `text` becomes non-empty and the canvas instantly shows
// exactly what they typed, in both editor and published views. The edit only
// ever writes `text`; `bind` and every style prop are untouched. The source
// hook is called only when we actually need the live value (blank text + a
// bind), so overridden and unbound nodes never fetch.
function useBoundText({ p, aux }) {
  const bind = (p.bind || '').trim();
  const authored = (p.text == null ? '' : String(p.text));
  const needLive = !authored && !!bind;
  const { data } = useSourcePage(needLive ? ((aux && aux.sourcePath) || '') : '');

  if (!needLive) return { text: authored, live: false, bind };

  const src = data || (aux && aux.contextPage) || null;
  const v = src ? src[bind] : undefined;
  let text = authored;
  if (typeof v === 'string' && v.trim() !== '') text = v;
  else if (typeof v === 'number') text = String(v);
  return { text, live: text !== authored, bind };
}

// Tooltip affordance (no layout impact): explains the binding state on hover.
function bindTitle(bind, authored) {
  if (!bind) return undefined;
  return authored
    ? 'Overriding getpagedata.' + bind + ' — clear the Text field to show the live value'
    : 'Showing live getpagedata.' + bind + ' — type in the Text field to override';
}

function HeadingView({ p, aux }) {
  const { text, bind } = useBoundText({ p, aux });
  const Tag = p.level || 'h2';
  return (
    <div className="l-heading" title={bindTitle(bind, (p.text == null ? '' : String(p.text)))}>
      <Tag style={{
        textAlign: p.align,
        color: p.color || undefined,
        fontWeight: p.weight || undefined,
        fontSize: Number(p.size) > 0 ? Number(p.size) + "px" : undefined,
        lineHeight: Number(p.lineHeight) > 0 ? p.lineHeight : undefined,
        letterSpacing: Number(p.letterSpacing) ? Number(p.letterSpacing) + "px" : undefined,
        marginTop: (Number(p.marginTop) || 0) + "px",
        marginBottom: (Number(p.marginBottom) || 0) + "px",
      }}>{text}</Tag>
    </div>
  );
}

// ------------------------------------------------------------
// Text / Paragraph — body copy, with OPTIONAL dynamic content binding
// ------------------------------------------------------------
// Same contract as HeadingView (see useBoundText): blank authored text + a
// `bind` shows the live course value on the canvas; typing overrides it. Only
// `text` is written on edit — alignment, size, weight, line-height,
// letter-spacing and margins are untouched, so no formatting/layout shift.
function TextView({ p, aux }) {
  const { text, bind } = useBoundText({ p, aux });
  return (
    <div className="l-text" title={bindTitle(bind, (p.text == null ? '' : String(p.text)))}>
      <p style={{
        textAlign: p.align,
        fontSize: (Number(p.size) || 16) + "px",
        color: p.color || undefined,
        fontWeight: p.weight || undefined,
        lineHeight: Number(p.lineHeight) > 0 ? p.lineHeight : 1.6,
        letterSpacing: Number(p.letterSpacing) ? Number(p.letterSpacing) + "px" : undefined,
        marginTop: (Number(p.marginTop) || 0) + "px",
        marginBottom: (Number(p.marginBottom) || 0) + "px",
      }}>
        {text}
      </p>
    </div>
  );
}

// ------------------------------------------------------------
// Per-property binding resolver — for nodes that bind more than one field
// (Image: src + alt, Button: text + href). Same override rule as text:
// an authored value wins; when it's blank and a `bind*` key is set, the
// live getpagedata value is used. `specs` is [{ authored, key }]; returns
// the resolved strings in the same order. One shared source fetch, only
// when some property actually needs the live value.
function useBoundValues(specs, aux) {
  const needLive = specs.some((s) => !s.authored && s.key);
  const { data } = useSourcePage(needLive ? ((aux && aux.sourcePath) || '') : '');
  const src = data || (aux && aux.contextPage) || null;
  return specs.map(({ authored, key }) => {
    if (authored) return authored;
    if (key && src) {
      const v = src[key];
      if (typeof v === 'string' && v.trim() !== '') return v;
      if (typeof v === 'number') return String(v);
    }
    return authored;
  });
}

// ------------------------------------------------------------
// Image — with OPTIONAL dynamic binding of `src` and `alt`
// ------------------------------------------------------------
// Blank authored src/alt + a bindSrc/bindAlt key => the live course value is
// used; a non-blank authored value overrides. CONTENT ONLY — width, height,
// radius, alignment and the empty-state / linked markup are byte-for-byte the
// previous render, so no sizing or aspect-ratio shift.
function ImageView({ p, aux }) {
  const handles = aux && aux.handles;
  const [src, alt] = useBoundValues([
    { authored: (p.src == null ? '' : String(p.src)), key: (p.bindSrc || '').trim() },
    { authored: (p.alt == null ? '' : String(p.alt)), key: (p.bindAlt || '').trim() },
  ], aux);

  const w = p.width && String(p.width).trim() !== "" ? (isNaN(Number(p.width)) ? p.width : (Number(p.width) + "px")) : undefined;
  const h = p.height && String(p.height).trim() !== "" ? (isNaN(Number(p.height)) ? p.height : (Number(p.height) + "px")) : undefined;

  if (!src) {
    return (
      <figure className="l-image" style={{ margin: 0, textAlign: p.align }}>
        <span className="l-image-frame l-image-frame--empty" style={{ width: w, height: h }}>
          <div className="l-image-empty"
               style={{ width: w ? "100%" : undefined, height: h ? "100%" : undefined }}>
            <span className="badge">IMG</span>
            <div>Image placeholder</div>
            <small>Select this element, then paste a URL or upload from your computer.</small>
          </div>
          {handles}
        </span>
      </figure>
    );
  }
  const img = (
    <img
      src={src}
      alt={alt || ""}
      style={{
        width: w, height: h, maxWidth: "100%",
        borderRadius: (Number(p.radius) || 0) + "px",
        display: "block",
      }}
    />
  );
  const linked = p.href
    ? <a href={p.href} target={p.target || "_self"} rel={p.target === "_blank" ? "noopener noreferrer" : undefined}
         onClick={(e) => e.preventDefault()}>{img}</a>
    : img;
  return (
    <figure className="l-image" style={{ margin: 0, textAlign: p.align }}>
      <span className="l-image-frame">
        {linked}
        {handles}
      </span>
      {p.caption ? <figcaption style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{p.caption}</figcaption> : null}
    </figure>
  );
}

// ------------------------------------------------------------
// Button — with OPTIONAL dynamic binding of label `text` and `href`
// ------------------------------------------------------------
// Blank authored text/href + a bindText/bindHref key => the live course value
// is used; a non-blank authored value overrides. CONTENT ONLY — padding,
// colours, weight, border and radius render exactly as before. (Apply/Enroll
// CTAs use the dedicated design-system `dynamic-cta` atom with SKU-driven
// URLs; this binding is for generic buttons/links.)
function ButtonView({ p, aux }) {
  const [text, href] = useBoundValues([
    { authored: (p.text == null ? '' : String(p.text)), key: (p.bindText || '').trim() },
    { authored: (p.href == null ? '' : String(p.href)), key: (p.bindHref || '').trim() },
  ], aux);

  const sizes = { sm: "6px 14px", md: "10px 20px", lg: "14px 28px" };
  const bw = Number(p.borderWidth) || 0;
  return (
    <div className="l-button" style={{ textAlign: p.align }}>
      <a href={href || "#"} target={p.target || "_self"}
         rel={p.target === "_blank" ? "noopener noreferrer" : undefined}
         onClick={(e) => e.preventDefault()}
         style={{
           background: p.background,
           color: p.color,
           padding: sizes[p.size] || sizes.md,
           borderRadius: (Number(p.radius) || 0) + "px",
           fontWeight: p.weight || undefined,
           border: bw ? bw + "px solid " + (p.borderColor || "#1f2430") : "0",
           display: p.fullWidth ? "block" : "inline-block",
           width: p.fullWidth ? "100%" : undefined,
           textAlign: "center",
         }}>{text}</a>
    </div>
  );
}

// ------------------------------------------------------------
// What You'll Learn — shared H2 + per-course body (no raw HTML)
// ------------------------------------------------------------
// The H2 is a single GLOBAL value shared across every course: it comes from
// the Global Course Content source (`resolveWhatYouLearnHeading`), NOT from
// the node's props, so it can never be edited per course and one change
// updates every page at once.
//
// The body IS per-course: authored via a `bullets` textarea (one item per
// line), with an optional `bind` to pull the list from a getpagedata field.
// If a bound body contains HTML list markup it is rendered as-is; otherwise
// each line becomes a <li>. The canvas shows a clean disc bullet list with
// reference styling.
function WhatYouLearnView({ p, aux }) {
  // Fixed/shared heading — single global source, never a per-course prop.
  const title = resolveWhatYouLearnHeading();

  const bindBody = (p.bind || '').trim();
  const authoredBullets = p.bullets == null ? '' : String(p.bullets);

  const needBody = !authoredBullets && bindBody;
  const { data } = useSourcePage(needBody ? ((aux && aux.sourcePath) || '') : '');
  const src = data || (aux && aux.contextPage) || null;

  let raw = authoredBullets;
  if (needBody && src) { const v = src[bindBody]; if (typeof v === 'string') raw = v; }

  const isHtml = /<\/?(ul|ol|li|p|div|br|strong|em)\b/i.test(raw);
  const items = isHtml ? null : raw.split('\n').map((s) => s.trim()).filter(Boolean);

  return (
    <div className="l-wyl">
      {title ? <h2 className="l-wyl-title">{title}</h2> : null}
      {isHtml
        ? <div className="l-wyl-list l-wyl-html" dangerouslySetInnerHTML={{ __html: raw }} />
        : (items && items.length
            ? <ul className="l-wyl-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
            : null)}
    </div>
  );
}

// ============================================================
// Small helpers used across the type registry
// ============================================================

// Accepts number | {t,r,b,l} | undefined; always returns a full 4-side object.
// Kept lenient so legacy pages that stored `padding: 60` keep rendering.
function normalizeSides(v) {
  if (v == null || v === "") return { t: 0, r: 0, b: 0, l: 0 };
  if (typeof v === "number") return { t: v, r: v, b: v, l: v };
  if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) {
    const n = Number(v);
    return { t: n, r: n, b: n, l: n };
  }
  return {
    t: Number(v.t) || 0,
    r: Number(v.r) || 0,
    b: Number(v.b) || 0,
    l: Number(v.l) || 0,
  };
}
function sidesToCss(v) {
  const s = normalizeSides(v);
  return `${s.t}px ${s.r}px ${s.b}px ${s.l}px`;
}

// Convert common video share URLs into embeddable URLs; leave anything else alone.
function normalizeVideoUrl(url) {
  if (!url) return "";
  try {
    const yt = url.match(/(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
    if (yt) return "https://www.youtube.com/embed/" + yt[1];
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) return "https://player.vimeo.com/video/" + vm[1];
  } catch (e) {}
  return url;
}

// Reads a File as a data URL. Used for the Image/Video "Upload" button so a
// visual page can carry media inline without a working upload endpoint.
function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.readAsDataURL(file);
  });
}

// ============================================================
// TYPE REGISTRY
// ============================================================
const T = {
  section: {
    label: "Section", icon: "▬", container: true, layout: "vertical", allowRoot: true,
    defaultProps: {
      background: "#ffffff", backgroundImage: "",
      padding: { t: 60, r: 24, b: 60, l: 24 },
      maxWidth: 1100, minHeight: 0, textAlign: "left",
    },
    defaultChildren: () => [],
    fields: [
      { name: "padding",   label: "Padding (px)",             type: "sides",  group: "Layout" },
      { name: "maxWidth",  label: "Inner max-width (px)",     type: "number", group: "Layout", hint: "0 = full width" },
      { name: "minHeight", label: "Minimum height (px)",      type: "number", group: "Layout", hint: "0 = auto" },
      { name: "textAlign", label: "Text align",               type: "select", group: "Layout", options: ["left","center","right"] },
      { name: "background",      label: "Background color",   type: "color",  group: "Style" },
      { name: "backgroundImage", label: "Background image",   type: "file",   group: "Style", accept: "image/*", hint: "Paste a URL or upload." },
    ],
    style: (p) => ({
      background: p.background,
      backgroundImage: p.backgroundImage ? `url("${p.backgroundImage}")` : undefined,
      backgroundSize: p.backgroundImage ? "cover" : undefined,
      backgroundPosition: p.backgroundImage ? "center" : undefined,
      padding: sidesToCss(p.padding),
      textAlign: p.textAlign,
      minHeight: (Number(p.minHeight) || 0) ? Number(p.minHeight) + "px" : undefined,
    }),
    innerStyle: (p) => ({ maxWidth: Number(p.maxWidth) || "none", margin: "0 auto" }),
    className: "n-section", innerClassName: "n-section-inner",
  },
  row: {
    label: "Row", icon: "▤", container: true, layout: "horizontal",
    defaultProps: { gap: 20, align: "stretch", justify: "flex-start", wrap: "nowrap", reverse: false, minHeight: 0 },
    defaultChildren: () => [ makeNode("column"), makeNode("column") ],
    fields: [
      { name: "gap",       label: "Gap between columns (px)", type: "number", group: "Layout" },
      { name: "wrap",      label: "Wrap",                     type: "select", group: "Layout", options: ["nowrap","wrap"] },
      { name: "reverse",   label: "Reverse column order",     type: "checkbox", group: "Layout" },
      { name: "minHeight", label: "Minimum height (px)",      type: "number", group: "Layout", hint: "0 = auto" },
      { name: "justify", label: "Horizontal align",         type: "select", group: "Alignment",
        options: ["flex-start","center","flex-end","space-between","space-around","space-evenly"] },
      { name: "align",   label: "Vertical align",           type: "select", group: "Alignment",
        options: ["stretch","flex-start","center","flex-end"] },
    ],
    style: (p) => ({
      gap: (Number(p.gap) || 0) + "px",
      alignItems: p.align,
      justifyContent: p.justify || "flex-start",
      flexWrap: p.wrap,
      flexDirection: p.reverse ? "row-reverse" : "row",
      minHeight: (Number(p.minHeight) || 0) ? Number(p.minHeight) + "px" : undefined,
    }),
    className: "n-row",
  },
  column: {
    label: "Column", icon: "▎", container: true, layout: "vertical",
    defaultProps: {
      weight: 1, padding: { t: 0, r: 0, b: 0, l: 0 },
      background: "", radius: 0, minHeight: 0,
      alignItems: "stretch", justifyContent: "flex-start",
    },
    defaultChildren: () => [],
    fields: [
      { name: "weight",         label: "Width (flex weight)", type: "number", group: "Layout", hint: "1 = equal share; 2 = twice as wide" },
      { name: "minHeight",      label: "Min height (px)",     type: "number", group: "Layout", hint: "0 = auto" },
      { name: "padding",        label: "Inner padding (px)",  type: "sides",  group: "Layout" },
      { name: "justifyContent", label: "Vertical align",      type: "select", group: "Alignment",
        options: ["flex-start","center","flex-end","space-between","space-around"] },
      { name: "alignItems",     label: "Horizontal align",    type: "select", group: "Alignment",
        options: ["stretch","flex-start","center","flex-end"] },
      { name: "background",     label: "Background",          type: "color",  group: "Style" },
      { name: "radius",         label: "Corner radius (px)",  type: "number", group: "Style" },
    ],
    style: (p) => ({
      flex: Number(p.weight) || 1,
      padding: sidesToCss(p.padding),
      background: p.background || "transparent",
      borderRadius: (Number(p.radius) || 0) + "px",
      alignItems: p.alignItems || "stretch",
      justifyContent: p.justifyContent || "flex-start",
      minHeight: (Number(p.minHeight) || 0) ? Number(p.minHeight) + "px" : undefined,
    }),
    className: "n-column",
  },
  card: {
    label: "Card", icon: "▢", container: true, layout: "vertical",
    defaultProps: {
      background: "#ffffff", padding: { t: 20, r: 20, b: 20, l: 20 },
      radius: 8, border: true, borderColor: "#e4e6eb", shadow: false,
      width: "", minHeight: 0,
    },
    defaultChildren: () => [
      makeNode("heading", { text: "Card title", level: "h3" }),
      makeNode("text", { text: "Card description goes here." }),
    ],
    fields: [
      { name: "width",       label: "Width",              type: "text",     group: "Layout", hint: "e.g. 400, 400px, or 100%. Blank = auto." },
      { name: "minHeight",   label: "Min height (px)",    type: "number",   group: "Layout", hint: "0 = auto" },
      { name: "padding",     label: "Padding (px)",       type: "sides",    group: "Layout" },
      { name: "background",  label: "Background",         type: "color",    group: "Style" },
      { name: "radius",      label: "Corner radius (px)", type: "number",   group: "Style" },
      { name: "border",      label: "Border",             type: "checkbox", group: "Style" },
      { name: "borderColor", label: "Border color",       type: "color",    group: "Style" },
      { name: "shadow",      label: "Drop shadow",        type: "checkbox", group: "Style" },
    ],
    style: (p) => ({
      background: p.background,
      padding: sidesToCss(p.padding),
      borderRadius: (Number(p.radius) || 0) + "px",
      border: p.border ? "1px solid " + (p.borderColor || "#e4e6eb") : "none",
      boxShadow: p.shadow ? "0 4px 12px rgba(0,0,0,.08)" : "none",
      width: p.width || undefined,
      minHeight: (Number(p.minHeight) || 0) ? Number(p.minHeight) + "px" : undefined,
    }),
    className: "n-card",
  },
  heading: {
    label: "Heading", icon: "H", container: false,
    defaultProps: {
      text: "New heading", bind: "", level: "h2", align: "left", color: "",
      weight: "", size: 0, lineHeight: 0, letterSpacing: 0,
      marginTop: 0, marginBottom: 0,
    },
    fields: [
      { name: "text",  label: "Text",  type: "text",  group: "Content",
        hint: 'Leave blank to show the live Dynamic field below; type here to override it for this page.' },
      { name: "bind",  label: "Dynamic field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field key (e.g. "title", "course_name"). Shows live on the ' +
              'canvas when Text is blank; the Text field overrides it.' },
      { name: "level", label: "Level", type: "select", group: "Content", options: ["h1","h2","h3","h4","h5"] },
      { name: "align", label: "Alignment", type: "select", group: "Layout", options: ["left","center","right"] },
      { name: "marginTop",    label: "Margin top (px)",    type: "number", group: "Layout" },
      { name: "marginBottom", label: "Margin bottom (px)", type: "number", group: "Layout" },
      { name: "size",          label: "Font size (px)",    type: "number", group: "Typography", hint: "0 = default for level" },
      { name: "weight",        label: "Font weight",       type: "select", group: "Typography",
        options: ["", "300","400","500","600","700","800","900"] },
      { name: "lineHeight",    label: "Line height",       type: "number", group: "Typography", hint: "0 = default" },
      { name: "letterSpacing", label: "Letter spacing (px)", type: "number", group: "Typography" },
      { name: "color", label: "Color", type: "color", group: "Style" },
    ],
    render: (p, aux) => <HeadingView p={p} aux={aux} />,
  },
  text: {
    label: "Text", icon: "T", container: false,
    defaultProps: {
      text: "Write your paragraph here…", bind: "",
      align: "left", size: 16, color: "",
      weight: "", lineHeight: 1.6, letterSpacing: 0,
      marginTop: 0, marginBottom: 0,
    },
    fields: [
      { name: "text",   label: "Body",       type: "textarea", group: "Content",
        hint: 'Leave blank to show the live Dynamic field below; type here to override it for this page.' },
      { name: "bind",   label: "Dynamic field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field key (e.g. "field_meta_description", "course_name"). Shows ' +
              'live on the canvas when Body is blank; the Body field overrides it.' },
      { name: "align",  label: "Alignment",  type: "select",   group: "Layout", options: ["left","center","right","justify"] },
      { name: "marginTop",    label: "Margin top (px)",    type: "number", group: "Layout" },
      { name: "marginBottom", label: "Margin bottom (px)", type: "number", group: "Layout" },
      { name: "size",          label: "Font size (px)",    type: "number", group: "Typography" },
      { name: "weight",        label: "Font weight",       type: "select", group: "Typography",
        options: ["", "300","400","500","600","700","800"] },
      { name: "lineHeight",    label: "Line height",       type: "number", group: "Typography", hint: "e.g. 1.6" },
      { name: "letterSpacing", label: "Letter spacing (px)", type: "number", group: "Typography" },
      { name: "color", label: "Color", type: "color", group: "Style" },
    ],
    render: (p, aux) => <TextView p={p} aux={aux} />,
  },
  image: {
    label: "Image", icon: "IMG", container: false,
    defaultProps: {
      src: "", bindSrc: "", alt: "", bindAlt: "", caption: "",
      width: "", height: "",
      radius: 6, align: "left",
      href: "", target: "_self",
    },
    fields: [
      { name: "src",     label: "Image", type: "file", group: "Content", accept: "image/*", hint: "Paste a URL or upload from your computer." },
      { name: "bindSrc", label: "Dynamic image field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field whose value is an image URL. Used when Image above is blank; the Image field overrides it.' },
      { name: "alt",     label: "Alt text", type: "text", group: "Content" },
      { name: "bindAlt", label: "Dynamic alt field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field for the alt text. Used when Alt text above is blank.' },
      { name: "caption", label: "Caption",  type: "text", group: "Content" },
      { name: "href",    label: "Link URL (optional)", type: "url", group: "Content" },
      { name: "target",  label: "Open link in", type: "select", group: "Content", options: ["_self","_blank"] },
      { name: "width",   label: "Width",  type: "text",   group: "Layout", hint: "e.g. 400, 400px, or 100%" },
      { name: "height",  label: "Height", type: "text",   group: "Layout", hint: "leave blank to auto" },
      { name: "align",   label: "Alignment", type: "select", group: "Layout", options: ["left","center","right"] },
      { name: "radius",  label: "Corner radius (px)", type: "number", group: "Style" },
    ],
    render: (p, aux) => <ImageView p={p} aux={aux} />,
  },
  button: {
    label: "Button", icon: "BTN", container: false,
    defaultProps: {
      text: "Click me", bindText: "", href: "#", bindHref: "", target: "_self",
      align: "left", size: "md",
      background: "#2f81f7", color: "#ffffff",
      radius: 5, weight: "500",
      borderWidth: 0, borderColor: "#1f2430",
      fullWidth: false,
    },
    fields: [
      { name: "text",     label: "Text",     type: "text", group: "Content" },
      { name: "bindText", label: "Dynamic label field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field for the button label. Used when Text above is blank; Text overrides it.' },
      { name: "href",     label: "Link URL", type: "url",  group: "Content" },
      { name: "bindHref", label: "Dynamic link field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. A getpagedata field whose value is the target URL. Used when Link URL above is blank. ' +
              '(For Apply/Enroll CTAs use the CTA Button component — its URLs come from the course SKU.)' },
      { name: "target", label: "Open link in", type: "select", group: "Content", options: ["_self","_blank"] },
      { name: "align",     label: "Alignment", type: "select", group: "Layout", options: ["left","center","right"] },
      { name: "size",      label: "Size",      type: "select", group: "Layout", options: ["sm","md","lg"] },
      { name: "fullWidth", label: "Full width", type: "checkbox", group: "Layout" },
      { name: "background", label: "Background", type: "color",   group: "Style" },
      { name: "color",      label: "Text color", type: "color",   group: "Style" },
      { name: "weight",     label: "Font weight", type: "select", group: "Style",
        options: ["", "400","500","600","700","800"] },
      { name: "radius",      label: "Corner radius (px)", type: "number", group: "Style" },
      { name: "borderWidth", label: "Border width (px)",  type: "number", group: "Style" },
      { name: "borderColor", label: "Border color",       type: "color",  group: "Style" },
    ],
    render: (p, aux) => <ButtonView p={p} aux={aux} />,
  },
  video: {
    label: "Video", icon: "▶", container: false,
    defaultProps: {
      src: "", aspect: "16/9", radius: 6,
      autoplay: false, controls: true, loop: false, muted: true,
      width: "", height: "",
    },
    fields: [
      { name: "src", label: "Video", type: "file", group: "Content", accept: "video/*",
        hint: "Paste a YouTube/Vimeo/MP4 URL, or upload a video file." },
      { name: "width",  label: "Width",  type: "text", group: "Layout", hint: "e.g. 640, 640px, or 100%. Blank = full width." },
      { name: "height", label: "Height", type: "text", group: "Layout", hint: "Overrides aspect ratio when set." },
      { name: "aspect",   label: "Aspect ratio", type: "select", group: "Layout",
        options: ["16/9","4/3","1/1","21/9","9/16"] },
      { name: "radius",   label: "Corner radius (px)", type: "number",  group: "Style" },
      { name: "controls", label: "Show controls (uploaded files only)", type: "checkbox", group: "Playback" },
      { name: "autoplay", label: "Autoplay (uploaded files only)",       type: "checkbox", group: "Playback" },
      { name: "loop",     label: "Loop (uploaded files only)",           type: "checkbox", group: "Playback" },
      { name: "muted",    label: "Muted (uploaded files only)",          type: "checkbox", group: "Playback" },
    ],
    render: (p, aux) => {
      const handles = aux && aux.handles;
      const w = p.width && String(p.width).trim() !== "" ? (isNaN(Number(p.width)) ? p.width : (Number(p.width) + "px")) : undefined;
      const h = p.height && String(p.height).trim() !== "" ? (isNaN(Number(p.height)) ? p.height : (Number(p.height) + "px")) : undefined;
      const wrapStyle = {
        aspectRatio: h ? undefined : (p.aspect || "16/9"),
        borderRadius: (Number(p.radius) || 0) + "px",
        overflow: "hidden",
        width: w || "100%",
        height: h,
        position: "relative",
      };
      if (!p.src) {
        return (
          <div className="l-video-empty" style={wrapStyle}>
            <span className="badge">▶</span>
            <div>Video placeholder</div>
            <small>Select this element, then paste a URL or upload a video file.</small>
            {handles}
          </div>
        );
      }
      const isDataOrFile = /^(data:|blob:)/.test(p.src) || /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(p.src);
      if (isDataOrFile) {
        return (
          <div className="l-video" style={wrapStyle}>
            <video
              src={p.src}
              controls={!!p.controls}
              autoPlay={!!p.autoplay}
              loop={!!p.loop}
              muted={!!p.muted}
              playsInline
              style={{ width: "100%", height: "100%", display: "block", background: "#000" }}
            />
            {handles}
          </div>
        );
      }
      return (
        <div className="l-video" style={wrapStyle}>
          <iframe title="embed" src={normalizeVideoUrl(p.src)} allowFullScreen
                  style={{ width: "100%", height: "100%" }} />
          {handles}
        </div>
      );
    },
  },
  html: {
    label: "Raw HTML", icon: "</>", container: false,
    defaultProps: { html: "<div>Custom <strong>HTML</strong></div>" },
    fields: [
      { name: "html", label: "HTML source", type: "textarea", hint: "Rendered as-is." },
    ],
    render: (p) => <div className="l-html" dangerouslySetInnerHTML={{ __html: p.html || "" }} />,
  },
  divider: {
    label: "Divider", icon: "—", container: false,
    defaultProps: { color: "#e4e6eb", thickness: 1, style: "solid", marginTop: 8, marginBottom: 8 },
    fields: [
      { name: "color",     label: "Line color",   type: "color",  group: "Style" },
      { name: "thickness", label: "Thickness (px)", type: "number", group: "Style" },
      { name: "style",     label: "Style",        type: "select", group: "Style", options: ["solid","dashed","dotted"] },
      { name: "marginTop",    label: "Margin top (px)",    type: "number", group: "Layout" },
      { name: "marginBottom", label: "Margin bottom (px)", type: "number", group: "Layout" },
    ],
    render: (p) => (
      <div className="l-divider" style={{
        marginTop: (Number(p.marginTop) || 0) + "px",
        marginBottom: (Number(p.marginBottom) || 0) + "px",
      }}>
        <hr style={{
          border: 0,
          borderTop: (Number(p.thickness) || 1) + "px " + (p.style || "solid") + " " + (p.color || "#e4e6eb"),
          margin: 0,
        }} />
      </div>
    ),
  },
  spacer: {
    label: "Spacer", icon: "␣", container: false,
    defaultProps: { height: 40 },
    fields: [{ name: "height", label: "Height (px)", type: "number", group: "Layout" }],
    render: (p) => <div className="l-spacer" style={{ height: (Number(p.height)||40) + "px" }} />,
  },

  // ------------------------------------------------------------
  // Next Start Date — course start-date card. Navy header + two mentor
  // schedules, matching the design reference. All markup is scoped under
  // `.l-nsb` (styled in PageEditor.css) so its class names never leak into
  // other editor blocks; the fixed 365x162 size is applied by the template.
  // ------------------------------------------------------------
  "next-start-date": {
    label: "Next Start Date", icon: "🗓", container: false, dynamic: true,
    // Fully dynamic, read-only. The tree stores only the source binding; the
    // start date and every mentor schedule come live from the course record
    // via getpagedata (data.next_start_date + data.course_chats). No editable
    // text fields — per client spec the content is not hand-editable. Scoped
    // under `.l-nsb` in PageEditor.css.
    defaultProps: { sourcePath: "" },
    fields: [
      { name: "sourcePath", label: "Source page path", type: "text", group: "Content",
        hint: 'Course whose start date + weekly chat schedule to load, e.g. ' +
              '"courses/athlete-management-sports-agent". Auto-filled from the page source. ' +
              'All content is read-only and loads live from the backend.' },
    ],
    render: (p, aux) => (
      <DynamicNextStartDate sourcePath={p.sourcePath} contextPage={aux && aux.contextPage} />
    ),
  },

  // ------------------------------------------------------------
  // Dynamic Components — server-backed blocks
  // ------------------------------------------------------------
  // Each of these renders one of the existing course-page components.
  // The tree stores only { type, props }; the wrapper fetches live data
  // via the same Yii2 API those components already use.
  "dynamic-mentors": {
    label: "Mentor Cards", icon: "👥", container: false, dynamic: true,
    defaultProps: { sourceNid: "" },
    fields: [
      { name: "sourceNid", label: "Source course NID", type: "number", group: "Content",
        hint: "Course node id whose mentors to load (e.g. 505). Find nids in Manage Course Pages." },
    ],
    render: (p, aux) => (
      <DynamicMentorCarousel sourceNid={p.sourceNid} contextPage={aux && aux.contextPage} />
    ),
  },
  "dynamic-success-stories": {
    label: "Alumni / Success Stories", icon: "⭐", container: false, dynamic: true,
    // Vertical-photo card carousel. Alumni cards come live from the course
    // (coursepages/getsuccessstories). The H2 heading is NOT editable here — it
    // is a single global value from Global Course Content (`alumni_heading`), so
    // one admin edit updates every course page and it is never stored in this
    // page's JSON. Only the subtitle stays per-course.
    defaultProps: {
      sourceNid: "",
      subtitle: "SMWW graduates are scouting, analyzing, and running baseball operations " +
                "across the majors, the minors, and international leagues.",
    },
    fields: [
      { name: "subtitle", label: "Subtitle", type: "textarea", group: "Content" },
      { name: "sourceNid", label: "Source course NID", type: "number", group: "Content",
        hint: "Course node id whose alumni to load. Find nids in Manage Course Pages." },
    ],
    render: (p, aux) => (
      <DynamicSuccessStoriesCarousel
        sourceNid={p.sourceNid} subtitle={p.subtitle}
        contextPage={aux && aux.contextPage} />
    ),
  },
  "dynamic-testimonials": {
    label: "Testimonials", icon: "💬", container: false, dynamic: true,
    defaultProps: { sourceNid: "" },
    fields: [
      { name: "sourceNid", label: "Source course NID", type: "number", group: "Content",
        hint: "Course node id whose testimonials to load. Find nids in Manage Course Pages." },
    ],
    render: (p, aux) => (
      <DynamicTestimonials sourceNid={p.sourceNid} contextPage={aux && aux.contextPage} />
    ),
  },
  "dynamic-quick-form": {
    label: "Contact / Quick Form", icon: "📝", container: false, dynamic: true,
    defaultProps: { sourcePath: "" },
    fields: [
      { name: "sourcePath", label: "Source page path", type: "text", group: "Content",
        hint: 'Course page whose name/SKU the form uses, e.g. "courses/athlete-management-sports-agent". Blank = generic form.' },
    ],
    render: (p, aux) => (
      <DynamicQuickForm sourcePath={p.sourcePath} contextPage={aux && aux.contextPage} />
    ),
  },

  // ------------------------------------------------------------
  // Centre-column course pieces. These are path-keyed (one shared
  // pages/getpagedata payload) rather than nid-keyed, and each delegates to
  // the exact component Page.js uses, so the visual page reproduces the
  // source page instead of embedding a frozen copy of its HTML.
  // ------------------------------------------------------------
  "dynamic-course-overview": {
    label: "Course Overview", icon: "📄", container: false, dynamic: true,
    defaultProps: { sourcePath: "" },
    fields: [
      { name: "sourcePath", label: "Source page path", type: "text", group: "Content",
        hint: 'e.g. "courses/athlete-management-sports-agent". Renders that page\'s body via the same <Body> the live site uses.' },
    ],
    render: (p) => <DynamicCourseOverview sourcePath={p.sourcePath} />,
  },
  "dynamic-what-you-learn": {
    label: "What You'll Learn", icon: "🎓", container: false, dynamic: true,
    // Shared H2 + per-course body. The H2 is NOT a prop — it comes from the
    // Global Course Content source and is identical on every course, so it is
    // deliberately absent from defaultProps and from the Inspector fields
    // below. Only the body (bullets) is editable per course. A freshly dropped
    // node arrives with default bullets so the canvas is never empty.
    defaultProps: {
      bullets: [
        "The core concepts and fundamentals of the industry",
        "Practical, real-world skills employers actually look for",
        "How the business operates day to day, from the inside",
        "Building a professional network in the field",
        "How to turn this program into interviews and offers",
      ].join("\n"),
      bind: "",
    },
    fields: [
      { name: "bullets", label: "Bullet points (one per line)", type: "textarea", group: "Content",
        hint: "One item per line — no HTML tags needed. The heading is shared across all courses and is set globally." },
      { name: "bind", label: "Dynamic body field (getpagedata)", type: "text", group: "Content",
        hint: 'Optional. Pull the list from a course field (e.g. "body"). Used when Bullet points is blank; an HTML list is rendered as-is.' },
    ],
    render: (p, aux) => <WhatYouLearnView p={p} aux={aux} />,
  },
  "dynamic-alumni": {
    label: "Alumni", icon: "🏅", container: false, dynamic: true,
    defaultProps: { sourcePath: "" },
    fields: [
      { name: "sourcePath", label: "Source page path", type: "text", group: "Content",
        hint: "Renders the source course's alumni block." },
    ],
    render: (p) => <DynamicAlumni sourcePath={p.sourcePath} />,
  },
  "dynamic-course-cta": {
    label: "Course CTA", icon: "📣", container: false, dynamic: true,
    defaultProps: { sourcePath: "" },
    fields: [
      { name: "sourcePath", label: "Source page path", type: "text", group: "Content",
        hint: "Renders the source course's course_cta block." },
    ],
    render: (p) => <DynamicCourseCta sourcePath={p.sourcePath} />,
  },

  // Atomic CTA. One node type, two design-system variants (Apply / Enroll).
  // Appearance and target URL are NOT editable here — background, hover, text
  // and the SKU-driven href all come from ctaConfig.js. The only author-
  // facing choices are which variant, alignment, full-width, and which course
  // supplies the SKU (auto-seeded from the page's source on create / drop).
  "dynamic-cta": {
    label: "CTA Button", icon: "🔘", container: false, dynamic: true,
    // fullWidth defaults true so a freshly dropped CTA matches the existing
    // full-width Apply/Enroll buttons the course template lays down (which set
    // fullWidth explicitly). Only affects NEW drops — saved nodes keep their
    // stored value — and the author can still uncheck "Full width".
    defaultProps: { variant: "apply", sourcePath: "", align: "left", fullWidth: true, buttonText: "" },
    // Two draggable palette entries for this one node type, so both the Apply
    // and Enroll buttons can be placed directly. Each drop merges its `props`
    // over defaultProps, presetting the variant; the Inspector's Action
    // dropdown can still flip it afterwards.
    paletteVariants: [
      { key: "apply",  label: "Apply Button",  icon: "🔴", props: { variant: "apply" } },
      { key: "enroll", label: "Enroll Button", icon: "🔵", props: { variant: "enroll" } },
    ],
    // Field visibility is role-gated in the Inspector (see `adminOnly`):
    //  - Action (Apply ↔ Enroll) and Button Text: Administrator only. SAAdmin
    //    cannot switch the variant or edit the label.
    //  - The CTA target is NOT editable by anyone: there is deliberately no
    //    URL / source-path field. `sourcePath` stays on the node but is
    //    auto-seeded from the page source (DYNAMIC_SEED_PROP) and the href is
    //    generated from the course SKU via ctaHref().
    fields: [
      { name: "variant", label: "Action", type: "select", group: "Content",
        adminOnly: true,
        options: CTA_VARIANT_KEYS,
        hint: "Apply or Enroll. Colour is fixed by the design system." },
      { name: "buttonText", label: "Button text", type: "text", group: "Content",
        adminOnly: true,
        hint: "Overrides the label for this button. Leave blank to use the default (Apply Now / Enroll Now)." },
      { name: "align",     label: "Alignment",  type: "select", group: "Layout", options: ["left","center","right"] },
      { name: "fullWidth", label: "Full width", type: "checkbox", group: "Layout" },
    ],
    render: (p, aux) => (
      <DynamicCTA variant={p.variant} sourcePath={p.sourcePath}
                  align={p.align} fullWidth={p.fullWidth} buttonText={p.buttonText}
                  contextPage={aux && aux.contextPage}
                  readOnly={aux && aux.readOnly} />
    ),
  },
};

const CONTAINER_TYPES = ["section","row","column","card"];
const ELEMENT_TYPES = ["heading","text","image","button","video","divider","spacer","html"];
const DYNAMIC_TYPES = [
  "dynamic-mentors", "dynamic-quick-form",
  "dynamic-course-overview", "dynamic-what-you-learn",
  "dynamic-alumni", "dynamic-course-cta",
  "dynamic-success-stories", "dynamic-testimonials",
  "dynamic-cta",
  // Next Start Date is now a live, read-only dynamic block (start date +
  // weekly chat schedule from the course record), not a hand-edited element.
  "next-start-date",
];

// Props each dynamic type wants seeded from the page-level source recorded on
// the root node, so palette drops arrive pre-bound instead of blank.
const DYNAMIC_SEED_PROP = {
  "dynamic-mentors": "sourceNid",
  "dynamic-success-stories": "sourceNid",
  "dynamic-testimonials": "sourceNid",
  "dynamic-quick-form": "sourcePath",
  "dynamic-course-overview": "sourcePath",
  "dynamic-alumni": "sourcePath",
  "dynamic-course-cta": "sourcePath",
  "dynamic-cta": "sourcePath",
  "next-start-date": "sourcePath",
};

// ============================================================
// Node factory + tree utilities
// ============================================================
const uid = () => Math.random().toString(36).slice(2, 10);

function makeNode(type, propsOverride) {
  const def = T[type];
  const props = { ...JSON.parse(JSON.stringify(def.defaultProps)), ...(propsOverride||{}) };
  const node = { id: uid(), type, props };
  if (def.container) node.children = def.defaultChildren ? def.defaultChildren() : [];
  return node;
}

function getNodeAt(root, path) {
  let n = root;
  for (const i of path) n = n.children[i];
  return n;
}
function withUpdatedAt(root, path, updater) {
  if (path.length === 0) return updater(root);
  const [head, ...rest] = path;
  const children = root.children.slice();
  children[head] = withUpdatedAt(children[head], rest, updater);
  return { ...root, children };
}
function removeAtPath(root, path) {
  const parent = path.slice(0, -1);
  const idx = path[path.length - 1];
  return withUpdatedAt(root, parent, node => ({
    ...node,
    children: node.children.filter((_, i) => i !== idx),
  }));
}
function insertAtPath(root, parentPath, idx, node) {
  return withUpdatedAt(root, parentPath, parent => {
    const children = parent.children.slice();
    children.splice(idx, 0, node);
    return { ...parent, children };
  });
}
function isAncestorPath(ancestor, descendant) {
  if (ancestor.length > descendant.length) return false;
  for (let i = 0; i < ancestor.length; i++) if (ancestor[i] !== descendant[i]) return false;
  return true;
}
function pathsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
function adjustPathAfterRemoval(path, removedPath) {
  const parent = removedPath.slice(0, -1);
  const removedIdx = removedPath[removedPath.length - 1];
  if (path.length < parent.length + 1) return path;
  for (let i = 0; i < parent.length; i++) if (path[i] !== parent[i]) return path;
  if (path[parent.length] > removedIdx) {
    const copy = path.slice();
    copy[parent.length] -= 1;
    return copy;
  }
  return path;
}
function moveInTree(root, fromPath, toParentPath, toIdx) {
  if (isAncestorPath(fromPath, toParentPath)) return root;
  const node = getNodeAt(root, fromPath);
  let next = removeAtPath(root, fromPath);
  const adjustedParent = adjustPathAfterRemoval(toParentPath, fromPath);
  let adjustedIdx = toIdx;
  const fromParent = fromPath.slice(0, -1);
  if (pathsEqual(fromParent, toParentPath) && toIdx > fromPath[fromPath.length - 1]) {
    adjustedIdx = toIdx - 1;
  }
  next = insertAtPath(next, adjustedParent, adjustedIdx, node);
  return next;
}
// The node the reorder controls (Up/Down arrows + drag) should actually act on.
// A block that is the SOLE child of its parent has no siblings to reorder among,
// so acting on it directly is a dead no-op (newIdx is always out of range) — yet
// that is exactly what the user grabs on the WIRED dynamic sections, each of
// which is a `section` wrapping a single dynamic block (mentors, alumni, CTA,
// quick-form, next-start-date). Their intent is to move the enclosing section.
// So walk up while the node is an only child (never past a top-level section /
// root child), and return that outermost single-child ancestor. Multi-child
// sections are unaffected — their inner blocks have siblings, so nothing bubbles
// and they keep reordering within the section exactly as before.
function reorderTargetPath(root, path) {
  let p = path;
  while (p.length > 1) {
    const parent = getNodeAt(root, p.slice(0, -1));
    if (!parent || !parent.children || parent.children.length !== 1) break;
    p = p.slice(0, -1);
  }
  return p;
}
function findPathById(root, id, path = []) {
  if (root.id === id) return path;
  if (!root.children) return null;
  for (let i = 0; i < root.children.length; i++) {
    const p = findPathById(root.children[i], id, path.concat(i));
    if (p) return p;
  }
  return null;
}

// ============================================================
// SAMPLE PAGE
// ============================================================
// Sample layout modeled on the live SMWW Athlete-Management / Sports-Agent
// course landing page — copy, colours, image URLs and section order sourced
// from an inspection of the production HTML.
//
// Ref: https://www.sportsmanagementworldwide.com/courses/athlete-management-sports-agent
//
// Overall structure:
//   Section 1 (light canvas)
//     Row (3 columns, stretch)
//       ├─ Mentors column          (6 mentor cards + orange CTA)
//       ├─ Center content column   (breadcrumb, hero, description card,
//       │                           navy Next-Start-Date card, all the
//       │                           long-form course sections + CTAs)
//       └─ Success Stories column  (8 story cards)
//   Section 2 (gray footer bar, red Apply Now button)
//
// Every node is a standard type-registry node, so the Inspector, drag-and-
// drop, upload/URL fields and 8-direction resize handles work on every
// piece without special-casing.
function makeSamplePage() {
  // Tiny local builders that just make the tree literal readable.
  const N = (type, props, children) => {
    const n = makeNode(type, props);
    if (children) n.children = children;
    return n;
  };
  const H  = (text, extra = {})       => N("heading", { text, ...extra });
  const TX = (text, extra = {})       => N("text",    { text, ...extra });
  const SP = (height)                 => N("spacer",  { height });
  const SEC = (props, kids)           => N("section", props, kids);
  const ROW = (props, kids)           => N("row",     props, kids);
  const COL = (props, kids)           => N("column",  props, kids);
  const CARD = (props, kids)          => N("card",    props, kids);
  const IMG = (props = {})            => N("image",   props);
  const BTN = (props = {})            => N("button",  props);
  const DIV = () => N("divider", { color: "#e4e6eb", thickness: 1, marginTop: 12, marginBottom: 12 });

  // Colours pulled directly from computed styles on the live page.
  const NAVY       = "#003C7D";  // Primary heading + navy card + Enroll button
  const BLUE       = "#2C99D2";  // Accent — breadcrumb + names + small links
  const ORANGE     = "#F3AC3A";  // "Connect with a career counselor" CTA
  const APPLY_RED  = "#B32116";  // Public site's Apply Now red
  const GRAY_TEXT  = "#374151";
  const DARK       = "#1f2937";
  const CANVAS_BG  = "#ffffff";  // Reference page uses a white body background
  const FOOTER_BG  = "#7c8391";

  // Reference page constrains the whole 3-column area to a ~1170px centered
  // container (matches the site-wide `#main { max-width: 1170px }` rule).
  // The section wrapper stays full-width (respecting the visual-page-wrap
  // override) while the inner content is centered within this cap.
  const CONTENT_MAX = 1170;

  // Image URLs pulled from the reference page's mentor/success-story blocks.
  const IMG_BASE  = "https://aisiiczsuo.cloudimg.io/v7/https://www.sportsmanagementworldwide.com/sites/default/files/";
  const MENTOR_IMG = (f) => IMG_BASE + "styles/mentor_block_crop/public/pictures/" + f;
  const STORY_IMG  = (f) => IMG_BASE + f;

  // --- Reusable card builders --------------------------------------------

  const mentorCard = (name, roles, imgPath) => CARD(
    { background: "#ffffff", padding: { t: 10, r: 10, b: 12, l: 10 },
      radius: 4, border: true, borderColor: "#d2d4d4", shadow: false },
    [
      IMG({ src: MENTOR_IMG(imgPath), alt: name,
            align: "center", width: "100%", radius: 3,
            href: "#", target: "_self" }),
      SP(8),
      H(name, { level: "h4", align: "center", color: BLUE,
                weight: "700", size: 15, marginBottom: 6, lineHeight: 1.2 }),
      ...roles.map((r) => TX(r, {
        align: "center", size: 12, color: GRAY_TEXT,
        lineHeight: 1.35, marginBottom: 2,
      })),
    ]
  );

  const storyCard = (name, title, imgFile) => CARD(
    { background: "#ffffff", padding: { t: 8, r: 10, b: 8, l: 10 },
      radius: 4, border: true, borderColor: "#d2d4d4", shadow: false },
    [
      ROW({ gap: 10, align: "center" }, [
        COL({ weight: 1 }, [
          IMG({ src: STORY_IMG(imgFile), alt: name,
                align: "left", width: "100%", radius: 3 }),
        ]),
        COL({ weight: 2 }, [
          H(name, { level: "h5", color: BLUE, weight: "700",
                    size: 13, marginBottom: 3, lineHeight: 1.2 }),
          TX(title, { size: 11, color: GRAY_TEXT, lineHeight: 1.4 }),
        ]),
      ]),
    ]
  );

  const checkTile = (label, highlighted) => COL(
    { weight: 1, padding: { t: 10, r: 6, b: 10, l: 6 },
      background: highlighted ? APPLY_RED : "#ffffff", radius: 4,
      alignItems: "center", justifyContent: "center" },
    [ TX(label, {
        size: 11,
        color: highlighted ? "#ffffff" : DARK,
        weight: "700", align: "center", lineHeight: 1.3,
      }) ]
  );

  // Standard site CTAs (mirror the button style from the live page).
  const applyBtn = () => BTN({
    text: "APPLY NOW", href: "/application/?course=AM400", align: "center",
    size: "md", background: APPLY_RED, color: "#ffffff",
    weight: "700", radius: 10, fullWidth: true,
  });
  const enrollBtn = () => BTN({
    text: "ENROLL NOW", href: "#", align: "center",
    size: "md", background: NAVY, color: "#ffffff",
    weight: "700", radius: 10, fullWidth: true,
  });

  // --- Mentors sidebar ----------------------------------------------------

  const mentorsColumn = COL({ weight: 1 }, [
    H("Mentors", { level: "h2", color: NAVY, weight: "700",
                   size: 18, marginBottom: 12 }),
    mentorCard("Dr Lynn Lashbrook",
      ["SMWW President & Founder", "Certified NFL Agent",
       "Former NCAA Athletic Director"],
      "picture-1472-1441814769.jpg?itok=6_KB0Otw"),
    SP(10),
    mentorCard("Joel Corry",
      ["CBS Agent's Take Writer", "Former Sports Agent"],
      "picture-1144-1441816140.jpg?itok=bKl7Bav8"),
    SP(10),
    mentorCard("Oscar Suarez", ["MLB Agent"],
      "picture-1831-1441816596.jpg?itok=zJca1Pq_"),
    SP(10),
    mentorCard("Bret Kanis", ["SMWW NBA Agent"],
      "picture-1427-1441823206.jpg?itok=kMMBh_W8"),
    SP(10),
    mentorCard("Rick Curran", ["NHL Agent", "The ORR Hockey Group"],
      "picture-2476-1448036812.png?itok=0WOT8dGm"),
    SP(10),
    mentorCard("Maikel Tsuji",
      ["FIFA Agent", "Former Professional Soccer Player"],
      "picture-2792-1485296250.jpg?itok=m6NRhSUq"),
    SP(14),
    BTN({
      text: "CONNECT WITH A CAREER COUNSELOR", href: "#", align: "center",
      size: "md", background: ORANGE, color: "#ffffff",
      weight: "700", radius: 7, fullWidth: true,
    }),
  ]);

  // --- Center content column ---------------------------------------------

  const centerColumn = COL({ weight: 3, padding: { t: 0, r: 10, b: 0, l: 10 } }, [
    TX("The Global Leader in Sports Agent Training  /  Athlete Management Certificate Course",
      { size: 13, color: BLUE, weight: "700", marginBottom: 4 }),
    H("Become a Sports Agent in Just 8 Weeks:",
      { level: "h1", color: NAVY, weight: "800", size: 30,
        marginTop: 0, marginBottom: 14, lineHeight: 1.2 }),
    TX("Train online with certified NFL, NBA, MLB, NHL, and FIFA agents. Represent athletes. Launch your agency career now — with the world's first sports agent school, trusted in 164 countries for over 30 years.",
      { size: 15, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 20 }),

    // ---- White description card
    CARD(
      { background: "#ffffff", padding: { t: 22, r: 24, b: 22, l: 24 },
        radius: 6, border: true, borderColor: "#e4e6eb", shadow: false },
      [
        H("Launch Your Career Representing Pro Athletes — No Law Degree Required",
          { level: "h3", color: DARK, weight: "700",
            size: 18, marginBottom: 12, lineHeight: 1.3 }),
        TX("Whether you're dreaming of signing NFL draft picks, negotiating MLB contracts, or managing global soccer stars, SMWW's 8-week online program is your fast track to becoming a certified sports agent.",
          { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
        TX("For over 30 years, the Sports Management Worldwide (SMWW) Sports Agent Course has helped thousands launch real careers — with success stories in every major league around the world.",
          { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
        TX("Join the only Sports Agent course led by industry legends like Dr. Lynn Lashbrook & Joel Corry, with unmatched credibility and results.",
          { size: 14, color: GRAY_TEXT, lineHeight: 1.6 }),
      ]
    ),

    SP(20),

    // ---- Navy Next-Start-Date card
    CARD(
      { background: NAVY, padding: { t: 22, r: 24, b: 22, l: 24 },
        radius: 6, border: false, shadow: false },
      [
        H("NEXT START DATE:  AUGUST 17",
          { level: "h3", color: "#ffffff", weight: "800",
            size: 22, marginBottom: 6, letterSpacing: 0.3 }),
        TX("Weekly Live Interactive Zoom Chats With Joel Corry & Lynn Lashbrook",
          { size: 14, color: "#ffffff", marginBottom: 14 }),
        ROW({ gap: 18, align: "center" }, [
          COL({ weight: 1 }, [
            TX("WEDNESDAYS   6 pm PT   |   9 pm ET",
              { size: 13, color: "#ffffff", weight: "700" }),
          ]),
          COL({ weight: 1 }, [
            TX("THURSDAYS   1 am GMT",
              { size: 13, color: "#ffffff", weight: "700", align: "right" }),
          ]),
        ]),
        SP(10),
        TX("▷  Click here to view in your timezone.",
          { size: 13, color: "#ffffff", marginBottom: 14 }),
        ROW({ gap: 6, align: "stretch", wrap: "wrap" }, [
          checkTile("✓ 8-week online course",         false),
          checkTile("✓ Access to SMWW network",       false),
          checkTile("✓ Resume enhancement",           false),
          checkTile("✓ Ongoing career advice",        false),
          checkTile("Class size limited to 30 students", true),
        ]),
      ]
    ),

    SP(28),

    // ---- Why Become a Sports Agent
    H("Why Become a Sports Agent? Because It's Possible — and We'll Show You How",
      { level: "h2", color: NAVY, weight: "800", size: 22,
        marginBottom: 10, lineHeight: 1.25 }),
    TX("If you've ever dreamed of becoming a sports agent, but didn't know where to start — or assumed law school was required — you're not alone.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("Good news: You don't need a law degree to become a successful sports agent. You just need the right training, mentorship, and industry access — and that's exactly what SMWW's 8-week online Athlete Management Course provides.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 16 }),
    IMG({ src: "https://aisiiczsuo.cloudimg.io/v7/https://www.sportsmanagementworldwide.com/sites/default/files/lynn-lashbrook-matt-moore.jpg",
          alt: "Dr. Lynn Lashbrook with Dallas Cowboys' KaVontae Turpin",
          align: "center", width: "100%", radius: 6 }),
    TX("(Photo above: SMWW President/NFLPA Agent, Dr. Lynn Lashbrook, with Dallas Cowboys' Wide Receiver/Kick Returner, KaVontae Turpin)",
      { size: 12, color: "#6b7280", lineHeight: 1.5, align: "center",
        marginTop: 6, marginBottom: 20 }),

    // ---- Train with the Best
    H("Train with the Best in the Business",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 8 }),
    TX("Led by real, certified agents actively working in the industry:",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("• Dr. Lynn Lashbrook (NFL)", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Joel Corry (NFL & NBA)",   { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Oscar Suarez (MLB)",       { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Bret Kanis (NBA, WNBA, FIBA)", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Rick Curran (NHL)",        { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Maikel Tsuji (FIFA)",      { size: 14, color: GRAY_TEXT, marginBottom: 10 }),
    TX("These mentors have negotiated multi-million-dollar contracts and represented first-round picks, Olympians, and athletes in every major league.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("Each week, you'll connect with them live via Zoom — not pre-recorded videos. These conversations are personal, interactive, and career-changing.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 20 }),

    // ---- What Happens After You Finish
    H("What Happens After You Finish?",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 8 }),
    TX("You won't just walk away with knowledge — you'll graduate with:",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("• A professional resume and cover letter, optimized for the sports industry",
      { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• A reference from your certified agent mentors",
      { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• An invitation to join SMWW Agency as an Agent Advisor and begin representing athletes",
      { size: 14, color: GRAY_TEXT, marginBottom: 10 }),
    TX("Our graduates now represent clients in every top league worldwide — including the NFL, NBA, NHL, MLB, MLS, WNBA, EPL, NWSL, IPL, UFC, FIBA, and more.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 20 }),

    // ---- How to Become a Sports Agent
    H("How to Become a Sports Agent",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 8 }),
    TX("The Athlete Management Course is a step-by-step blueprint to launch your career in athlete representation. You'll learn to:",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("• Recruit your first client with confidence and integrity", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Navigate contract negotiations and player endorsements", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Develop and maintain athlete relationships", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Handle league registrations, compliance, and regulations", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Build a personal brand and agency business", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Represent athletes in NFL, NBA, MLB, NHL, FIFA, UFC, Rugby, Cricket, Tennis, and more",
      { size: 14, color: GRAY_TEXT, marginBottom: 20 }),

    // ---- Course Topics
    H("Course Topics Include:",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 8 }),
    TX("• Recruiting strategies that work — even without existing connections",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Draft prep: combines, tryouts, pro days",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Legal and ethical issues in representation",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Contract negotiation for rookies, veterans, and free agents",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Endorsements and off-the-field brand building",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• How to build your reputation and stand out as a trusted agent",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Career counseling and post-athletic career transitions for clients",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Day-to-day operations and best practices for managing a sports agency",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 20 }),

    // ---- Where Graduates Work
    H("Where SMWW Agent Graduates Work:",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 8 }),
    TX("This course prepares you to work in every major sport around the world — whether your goal is to represent first-round NFL picks or rising soccer stars in Europe. SMWW-trained agents are thriving in roles such as:",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    ROW({ gap: 20, align: "flex-start" }, [
      COL({ weight: 1 }, [
        TX("• NFL Agent",                     { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• NBA or FIBA Basketball Agent",  { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• MLB or NPB Baseball Agent",     { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• NHL Agent",                     { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• FIFA/UEFA Soccer Agent",        { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• MLS or NWSL Agent",             { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
      ]),
      COL({ weight: 1 }, [
        TX("• Rugby or Cricket Agent",        { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• UFC, MMA, or Boxing Manager",   { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• Tennis or Golf Representative", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• Olympic & Paralympic Athlete Agent", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
        TX("• Extreme / Action Sports Agent", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
      ]),
    ]),
    SP(10),
    TX("Join the thousands of SMWW graduates making an impact in athlete representation across 164+ countries.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 14 }),
    ROW({ gap: 12, align: "center" }, [
      COL({ weight: 1 }, [ applyBtn() ]),
      COL({ weight: 1 }, [ enrollBtn() ]),
    ]),

    SP(28),

    // ---- Mentor bios
    H("Meet Your Mentor: Dr. Lynn Lashbrook — A Pioneer in the Sports Agent Industry",
      { level: "h3", color: NAVY, weight: "700", size: 18,
        marginBottom: 8, lineHeight: 1.3 }),
    TX("NFLPA Certified Agent  |  SMWW Founder  |  Global Talent Builder",
      { size: 13, color: BLUE, weight: "700", marginBottom: 10 }),
    TX("Dr. Lynn Lashbrook is one of the most experienced and respected sports agents in the world. As a longtime NFLPA-certified agent and the founder of Sports Management Worldwide, he has spent over 30 years training, mentoring, and launching the careers of thousands of sports agents who now work in every major league around the globe.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("Lynn doesn't just teach theory — he's been in the trenches. He worked with the legendary agency that represented Jerry Rice, William \"The Refrigerator\" Perry, and Elvis Grbac. More recently, he mentored two SMWW alumni who discovered and signed Kavontae Turpin, now a standout wide receiver for the Dallas Cowboys.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("His teaching style is honest, passionate, and deeply motivational. Every week, he joins live video chats with students — sharing real-world strategies, insider knowledge, and hard-earned lessons that only someone with decades of experience can offer.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("Click for more on Dr. Lashbrook →",
      { size: 13, color: BLUE, weight: "700", marginBottom: 20 }),

    H("Joel Corry",
      { level: "h3", color: NAVY, weight: "700", size: 18, marginBottom: 6 }),
    TX("Former NFL & NBA Agent  |  CBSSports Analyst  |  Salary Cap Expert",
      { size: 13, color: BLUE, weight: "700", marginBottom: 10 }),
    TX("With 20+ years mentoring aspiring sports agents and a 16-year career representing legends like Shaquille O'Neal, Hakeem Olajuwon, John Randle, and Ronnie Lott, Joel Corry is one of the most respected names in athlete representation.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("Now a leading CBSSports.com analyst and CBA expert, Joel is regularly featured on sports radio and serves as a trusted voice in sports business litigation. He simplifies contract strategy, teaches how to negotiate with confidence, and helps SMWW students understand the financial backbone of every league.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("Click for more on Joel Corry →",
      { size: 13, color: BLUE, weight: "700", marginBottom: 24 }),

    // ---- Success Stories highlight
    H("SMWW Success Stories: From Course to Career as a Sports Agent",
      { level: "h3", color: NAVY, weight: "700", size: 18,
        marginBottom: 8, lineHeight: 1.3 }),
    TX("Every one of these standout agents began right here — with SMWW's Athlete Management course. Today, they represent athletes in the NFL, NBA, MLB, MLS, NHL, and leagues around the world.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("• Ray Haija signed Travis Hunter, the #2 overall pick in the 2025 NFL Draft (Jacksonville Jaguars)",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Brian Lowe helped discover and sign Kavontae Turpin, now with the Dallas Cowboys",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Nik Lubisich, a former minor leaguer & SMWW graduate, represents MLB players including All-Star Zach McKinstry, Ryan Thompson, and top prospect Noble Meyer",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Adam Klimek represented Alfonzo McKinnie (Toronto Raptors, LA Lakers)",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Steve Banks signed NBA 1st-rounder Rodney Stuckey and went on to run his own NBA agency",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Denny Jones signed Jose Acuna (Philadelphia Eagles) — all while still a full-time college student",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 4 }),
    TX("• Greg Linton became an NFLPA-certified agent and was featured in the documentary Late Rounders",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.55, marginBottom: 12 }),
    CARD(
      { background: "#f5f7fb", padding: { t: 16, r: 20, b: 16, l: 20 },
        radius: 6, border: true, borderColor: "#dbe4ef", shadow: false },
      [
        TX("\"Lynn changed my life. He gave me the tools, the confidence, and the belief that I could do this — and now I'm working in the field I love.\"",
          { size: 14, color: DARK, weight: "500", lineHeight: 1.55, marginBottom: 6 }),
        TX("— Ray Haija, SMWW Graduate & NFL Agent",
          { size: 12, color: BLUE, weight: "700" }),
      ]
    ),

    SP(28),

    // ---- After Graduation
    H("What Happens After You Graduate?",
      { level: "h2", color: NAVY, weight: "800", size: 22, marginBottom: 10 }),
    TX("Completing the Athlete Management course is just the beginning. As a graduate, you'll be invited to become an SMWW Agent Advisor — our exclusive next step for launching your sports agent career.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("For a small yearly fee, Agent Advisors:",
      { size: 14, color: GRAY_TEXT, marginBottom: 6 }),
    TX("• Recruit and represent professional athletes in any sport", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Co-represent clients with certified agents in the NFL, NBA, MLB, NHL, MLS, and FIFA", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Access exclusive scouting reports, agency tools, and marketing materials", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Share in commissions from athletes you help sign", { size: 14, color: GRAY_TEXT, marginBottom: 3 }),
    TX("• Leverage SMWW's global network and decades of proven success", { size: 14, color: GRAY_TEXT, marginBottom: 12 }),
    TX("No law degree required. No hidden costs. Just the tools, training, and mentorship to start building your roster — today.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 14 }),
    BTN({
      text: "BECOME AN SMWW AGENT ADVISOR", href: "/program/agent-advisor",
      align: "center", size: "md", background: APPLY_RED, color: "#ffffff",
      weight: "700", radius: 10, fullWidth: true,
    }),

    SP(28),

    // ---- Resume Redesign
    H("Resume Redesign Service",
      { level: "h2", color: NAVY, weight: "800", size: 22, marginBottom: 10 }),
    TX("Getting the skills is step one — getting noticed is step two.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("At SMWW, we don't just train you to succeed — we help you market yourself to sports industry decision-makers. We'll revamp your resume to highlight your new credentials, include references from top agents, and optimize it for online searches so hiring managers can find you fast.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 8 }),
    TX("Our goal? To make you the next SMWW success story. And we'll do everything we can to get you there.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 16 }),
    ROW({ gap: 12, align: "center", wrap: "wrap" }, [
      COL({ weight: 1 }, [ applyBtn() ]),
      COL({ weight: 1 }, [ enrollBtn() ]),
      COL({ weight: 1 }, [ BTN({
        text: "SPORTS AGENT SALARIES", href: "#", align: "center",
        size: "md", background: NAVY, color: "#ffffff",
        weight: "700", radius: 10, fullWidth: true,
      }) ]),
      COL({ weight: 1 }, [ BTN({
        text: "FAQ", href: "#", align: "center",
        size: "md", background: NAVY, color: "#ffffff",
        weight: "700", radius: 10, fullWidth: true,
      }) ]),
    ]),

    SP(28),

    // ---- Course Details
    H("COURSE DETAILS",
      { level: "h2", color: NAVY, weight: "800", size: 22,
        marginBottom: 12, letterSpacing: 0.5 }),
    H("Live Weekly Zoom Chats with Real Agents & Athletes",
      { level: "h3", color: NAVY, weight: "700", size: 16, marginBottom: 6 }),
    TX("Each week, you'll join live Zoom sessions with Dr. Lashbrook, Joel Corry, SMWW mentors, and guest agents across every major sport. These interactive chats let you ask questions, build your network, and learn directly from the pros. Can't make it live? All sessions are recorded so you never miss out. Alumni love them so much — they keep coming back!",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 16 }),
    H("Flexible Online Learning, Designed for Sports Careers",
      { level: "h3", color: NAVY, weight: "700", size: 16, marginBottom: 6 }),
    TX("This 8-week course is 100% online, combining weekly readings, quizzes, discussion boards, and real-world projects. You'll learn at your own pace while engaging with fellow future agents and industry pros — all from anywhere in the world. It's everything you need to launch your sports agent career.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 16 }),
    H("Join a Global Network of Future Sports Executives",
      { level: "h3", color: NAVY, weight: "700", size: 16, marginBottom: 6 }),
    TX("SMWW has students and graduates now working in sports business in over 164 countries. Our courses are internationally focused, with assignments tailored to your country or league of interest.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 16 }),

    DIV(),

    // ---- Certificate
    H("Course Certificate & Resume Reference:",
      { level: "h2", color: NAVY, weight: "800", size: 20, marginBottom: 10 }),
    TX("Successful graduates of the Sports Management Worldwide courses will be sent a SMWW Certificate of Accomplishment. This certificate represents not only your achievements in the course but also demonstrates your personal commitment to self-improvement and ability to \"self train\" yourself for higher positions within the sports business industry.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6, marginBottom: 10 }),
    TX("In addition to your certificate, you will be given the stamp of approval by SMWW. This approval includes a personal reference you may add to your resume with contact information for Dr. Lynn Lashbrook and a reference to the mentors of this course.",
      { size: 14, color: GRAY_TEXT, lineHeight: 1.6 }),
  ]);

  // --- Success stories sidebar -------------------------------------------

  const successColumn = COL({ weight: 1 }, [
    H("Success Stories", { level: "h2", color: NAVY, weight: "700",
                           size: 18, marginBottom: 12 }),
    storyCard("Cole Waggoner",       "Middle Tennessee State Football Recruiting & Scouting Intern", "Cole%20Waggoner.jpg"),
    SP(8),
    storyCard("Abbey Steeneveld",    "NFL Event Operations",                                        "Abbey%20Steeneveld.jpeg"),
    SP(8),
    storyCard("George Kyriazakos",   "NFLPA Certified Agent",                                       "George%20Kyriazakos.jpg"),
    SP(8),
    storyCard("John Oluyole",        "NFL Agent",                                                    "picture-1631-1441602821.jpg"),
    SP(8),
    storyCard("Mat Billette",        "Volta Sports & Entertainment Director - Partnership",         "mat%20billette.jpg"),
    SP(8),
    storyCard("Andrew Vasilatos",    "Quantum Hockey Agency, Player Agent",                          "Andrew%20Vasilatos.jpg"),
    SP(8),
    storyCard("Michael Marlier",     "AND Sports and Entertainment VP of Football Operations",       "Michael%20Marlier%20Photo.jpg"),
    SP(8),
    storyCard("Kent Strobl",         "Creative Artists Agency",                                      "Kent%20Strobl.jpg"),
  ]);

  // --- Root ---------------------------------------------------------------

  return {
    id: uid(), type: "root", props: {}, children: [
      SEC(
        { background: CANVAS_BG,
          padding: { t: 24, r: 16, b: 40, l: 16 },
          maxWidth: CONTENT_MAX, textAlign: "left" },
        [ ROW({ gap: 20, align: "flex-start" },
              [ mentorsColumn, centerColumn, successColumn ]) ]
      ),
      SEC(
        { background: FOOTER_BG,
          padding: { t: 18, r: 20, b: 18, l: 20 },
          textAlign: "center", maxWidth: 0 },
        [ BTN({
            text: "APPLY NOW", href: "/application/?course=AM400",
            align: "center", size: "lg",
            background: APPLY_RED, color: "#ffffff",
            weight: "700", radius: 6,
          }) ]
      ),
    ],
  };
}

// ============================================================
// APP
// ============================================================
const STORAGE_KEY = "pageBuilder:v2";

// Optional props (all backwards-compatible; no props => standalone /page-editor behaviour):
//   initialTree — seed tree from a caller (e.g. CMS-loaded page body). Skips localStorage.
//   onSave      — callback (tree) => void; when set, a "Save" button appears in the topbar.
//   readOnly    — when true, render the tree without editor chrome (drag/toolbar/drop zones).
//   storageKey  — namespace localStorage per page (e.g. "pageBuilder:draft:<nid>").
// ============================================================
// Undo / Redo — history reducer
// ============================================================
// Every tree change goes through this reducer so past/future stay in sync
// with the current tree atomically. Rapid successive edits that share a
// `coalesceKey` (e.g. dragging a resize handle, typing into a text field)
// are merged into a single history entry — the tree from *before* the
// burst started is kept, and everything until the burst ends counts as
// one undo step. Explicit actions (add / delete / duplicate / move /
// drop / import / reset) always create a new entry.
const HISTORY_CAP = 100;
const COALESCE_MS = 800;
// Debounce window for backend auto-save: rapid edits within this quiet period
// are coalesced into a single onSave/saveVisualPage request.
const AUTOSAVE_MS = 1200;

function historyReducer(state, action) {
  switch (action.type) {
    case 'SET_TREE': {
      const { updater, key, ts } = action;
      const nextTree = typeof updater === 'function' ? updater(state.tree) : updater;
      if (nextTree === state.tree) return state;
      const canCoalesce = key !== null && state.lastKey === key && (ts - state.lastTs) < COALESCE_MS;
      const past = canCoalesce
        ? state.past
        : (state.past.length >= HISTORY_CAP ? state.past.slice(1) : state.past).concat([state.tree]);
      return {
        tree: nextTree, past, future: [],
        lastKey: key, lastTs: ts,
      };
    }
    case 'REPLACE_TREE': {
      // Import / Load sample / Clear — always undoable, no coalescing.
      if (action.tree === state.tree) return state;
      const past = (state.past.length >= HISTORY_CAP ? state.past.slice(1) : state.past).concat([state.tree]);
      return { tree: action.tree, past, future: [], lastKey: null, lastTs: 0 };
    }
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const prev = state.past[state.past.length - 1];
      return {
        tree: prev,
        past: state.past.slice(0, -1),
        future: [state.tree, ...state.future],
        lastKey: null, lastTs: 0,
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      return {
        tree: next,
        past: state.past.concat([state.tree]),
        future: state.future.slice(1),
        lastKey: null, lastTs: 0,
      };
    }
    default: return state;
  }
}

function PageEditor({ initialTree, onSave, onSaveAsNew, onPublish, published, readOnly, storageKey, contextPage, pageType, isAdministrator } = {}) {
  const storageKeyEff = storageKey || STORAGE_KEY;
  const [state, dispatch] = useReducer(historyReducer, { initialTree, storageKeyEff }, ({ initialTree, storageKeyEff }) => {
    let tree = null;
    if (initialTree) tree = initialTree;
    else {
      try {
        const raw = localStorage.getItem(storageKeyEff);
        if (raw) tree = JSON.parse(raw);
      } catch (e) {}
    }
    if (!tree) tree = makeSamplePage();
    return { tree, past: [], future: [], lastKey: null, lastTs: 0 };
  });
  const tree = state.tree;
  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;

  // Small convenience wrappers so callers don't have to build action shapes.
  // mutateTree takes the same functional form the old `setTree(t => ...)`
  // callers used; pass { coalesceKey: 'foo' } to fold rapid same-key edits
  // into one history entry.
  const mutateTree = useCallback((updater, opts) => {
    dispatch({
      type: 'SET_TREE',
      updater,
      key: (opts && opts.coalesceKey) ? opts.coalesceKey : null,
      ts: Date.now(),
    });
  }, []);
  const replaceTree = useCallback((nextTree) => {
    dispatch({ type: 'REPLACE_TREE', tree: nextTree });
  }, []);
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
  const [saveState, setSaveState] = useState({ status: 'idle', message: '' });
  // Publish state (draft vs published). Seeded from the host page's stored
  // `status` and updated in place after a Publish/Unpublish so the button label
  // reflects the change without a full reload. Only meaningful when `onPublish`
  // is wired (the admin editor); the read-only public view never renders it.
  const [isPublished, setIsPublished] = useState(!!published);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");

  const dragRef = useRef({ mode: null, type: null, fromPath: null, preset: null });
  // The tree the editor was seeded with (from `initialTree`/localStorage). Used
  // to skip auto-saving on a plain page open — only real user changes persist.
  const autosaveSeedRef = useRef(tree);

  // Auto-persist the working tree to localStorage ONLY for the standalone
  // editor, which has no Save button and no backend — localStorage is its only
  // store. When the host wires an explicit Save (`onSave`, e.g. the Visual Page
  // Editor), the working tree is auto-saved to the backend instead (see the
  // debounced auto-save effect below), never to localStorage.
  useEffect(() => {
    if (readOnly || onSave) return;
    try { localStorage.setItem(storageKeyEff, JSON.stringify(tree)); } catch (e) {}
  }, [tree, storageKeyEff, readOnly, onSave]);

  const selectedPath = useMemo(() => selectedId ? findPathById(tree, selectedId) : null, [tree, selectedId]);
  const selectedNode = selectedPath ? getNodeAt(tree, selectedPath) : null;

  // `preset` (optional) carries default-prop overrides for palette entries
  // that represent a variant of a single type — e.g. the Apply vs Enroll CTA
  // buttons both drop a `dynamic-cta` node but preset a different `variant`.
  const startPaletteDrag = (e, type, preset) => {
    dragRef.current = { mode: "palette", type, fromPath: null, preset: preset || null };
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", "palette:" + type);
  };
  const startNodeDrag = (e, path) => {
    e.stopPropagation();
    // Drag the nearest section-level node when the grabbed node is a sole child
    // (the dynamic block inside a WIRED single-child section). Without this, the
    // block would be dropped on its own into another container, leaving an empty
    // section behind; bubbling to the section makes drag-reorder move it as one
    // clean unit. Multi-child blocks resolve to themselves (drag unchanged).
    dragRef.current = { mode: "move", type: null, fromPath: reorderTargetPath(tree, path) };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "node");
  };

  const onDropZoneOver = (e, parentPath, idx) => {
    if (dragRef.current.mode === "move" && isAncestorPath(dragRef.current.fromPath, parentPath)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = dragRef.current.mode === "move" ? "move" : "copy";
    setDragOver({ parentPath, idx });
  };
  const onDropZoneDrop = (e, parentPath, idx) => {
    e.preventDefault();
    e.stopPropagation();
    const { mode, type, fromPath, preset } = dragRef.current;
    if (mode === "palette" && type) {
      // Dynamic components dropped from the palette inherit the page's source
      // course nid (recorded on the root node's props at create time) so they
      // render live data immediately instead of showing the "set a Source
      // course NID" placeholder. makeNode already accepts a props override, so
      // the drop path itself is unchanged. A palette-variant `preset` (e.g.
      // the Enroll CTA button) is merged on top so the dropped node arrives
      // preset to that variant.
      const seedProp = DYNAMIC_SEED_PROP[type];
      const seedValue = seedProp && tree.props ? tree.props[seedProp] : undefined;
      const override = {
        ...(seedValue ? { [seedProp]: seedValue } : {}),
        ...(preset || {}),
      };
      const node = makeNode(type, Object.keys(override).length ? override : undefined);
      mutateTree(t => insertAtPath(t, parentPath, idx, node));
      setSelectedId(node.id);
    } else if (mode === "move" && fromPath) {
      mutateTree(t => moveInTree(t, fromPath, parentPath, idx));
    }
    dragRef.current = { mode: null, type: null, fromPath: null, preset: null };
    setDragOver(null);
  };
  const onDragEnd = () => {
    dragRef.current = { mode: null, type: null, fromPath: null, preset: null };
    setDragOver(null);
  };

  const updateProps = useCallback((path, patch) => {
    // Coalesce continuous edits to the same node (drag resize, typing) into
    // one history entry — key = node path. Any different node / action
    // breaks the coalescing window.
    mutateTree(
      t => withUpdatedAt(t, path, node => ({ ...node, props: { ...node.props, ...patch } })),
      { coalesceKey: 'props:' + path.join('.') }
    );
  }, [mutateTree]);
  const removeNode = useCallback((path) => {
    if (path.length === 0) return;
    mutateTree(t => removeAtPath(t, path));
    setSelectedId(null);
  }, [mutateTree]);
  const duplicateNode = useCallback((path) => {
    if (path.length === 0) return;
    mutateTree(t => {
      const node = getNodeAt(t, path);
      const cloned = JSON.parse(JSON.stringify(node));
      const reid = (n) => { n.id = uid(); if (n.children) n.children.forEach(reid); };
      reid(cloned);
      const parent = path.slice(0, -1);
      const idx = path[path.length - 1];
      return insertAtPath(t, parent, idx + 1, cloned);
    });
  }, [mutateTree]);
  const moveNodeBy = useCallback((path, delta) => {
    if (path.length === 0) return;
    mutateTree(t => {
      // Reorder the nearest section-level node when the target is a sole child
      // (e.g. the dynamic block inside a WIRED single-child section), so its
      // Up/Down arrows move the whole section instead of no-op'ing. Multi-child
      // targets resolve to themselves and keep reordering in place.
      const effPath = reorderTargetPath(t, path);
      if (effPath.length === 0) return t;
      const parentPath = effPath.slice(0, -1);
      const idx = effPath[effPath.length - 1];
      const parent = getNodeAt(t, parentPath);
      const newIdx = idx + delta;
      if (newIdx < 0 || newIdx >= parent.children.length) return t;
      const target = delta > 0 ? newIdx + 1 : newIdx;
      return moveInTree(t, effPath, parentPath, target);
    });
  }, [mutateTree]);

  useEffect(() => {
    if (readOnly) return;
    const onKey = (e) => {
      const tag = document.activeElement && document.activeElement.tagName;
      const inField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      const mod = e.ctrlKey || e.metaKey;
      // Undo / Redo — Ctrl+Z, Ctrl+Y, Ctrl+Shift+Z. Fire even from within
      // inspector fields so the shortcut works while editing text.
      if (mod && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (mod && ((e.key === 'y' || e.key === 'Y') || ((e.key === 'z' || e.key === 'Z') && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      if (inField) return;
      if (!selectedPath) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeNode(selectedPath);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedPath, removeNode, readOnly, undo, redo]);

  const exportJson = () => JSON.stringify({ version: 2, tree }, null, 2);
  const downloadJson = () => {
    const blob = new Blob([exportJson()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "page-layout.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  const doImport = () => {
    try {
      const data = JSON.parse(importText);
      const nextTree = data.tree || (data.type === "root" ? data : null);
      if (!nextTree || !nextTree.children) throw new Error("Invalid: expected { tree: { type: 'root', children: [...] } }");
      replaceTree(nextTree);
      setShowImport(false); setImportText(""); setSelectedId(null);
    } catch (err) { window.alert("Import failed: " + err.message); }
  };
  const resetToSample = () => {
    if (!window.confirm("Reset to the sample page? You can undo if needed.")) return;
    replaceTree(makeSamplePage());
    setSelectedId(null);
  };
  const clearAll = () => {
    if (!window.confirm("Clear the page? You can undo if needed.")) return;
    replaceTree({ id: uid(), type: "root", props: {}, children: [] });
    setSelectedId(null);
  };

  const ctx = {
    selectedId, setSelectedId,
    hoveredId, setHoveredId,
    dragOver, onDropZoneOver, onDropZoneDrop,
    startNodeDrag, removeNode, duplicateNode, moveNodeBy,
    updateProps,
    readOnly: !!readOnly,
    // Dynamic-component data source: the page currently being viewed/edited.
    // Passed down through NodeView -> render(props, aux) so dynamic types
    // can fall back to the host page's nid when no explicit source is set.
    contextPage: contextPage || null,
    // Page-level course binding (recorded on the root node at create time).
    // Threaded into every node's render aux so content-bindable nodes (e.g.
    // headings with a `bind`) can pull live values from that course's
    // getpagedata without each node storing the source path itself.
    sourcePath: (tree.props && tree.props.sourcePath) || '',
  };

  const handleSave = async () => {
    if (!onSave) return;
    try {
      setSaveState({ status: 'saving', message: 'Saving…' });
      await onSave(tree);
      setSaveState({ status: 'saved', message: 'Saved' });
      setTimeout(() => setSaveState((s) => (s.status === 'saved' ? { status: 'idle', message: '' } : s)), 2000);
    } catch (err) {
      setSaveState({ status: 'error', message: 'Save failed: ' + (err && err.message ? err.message : 'unknown') });
    }
  };

  // Debounced backend auto-save. Persists every change through the SAME
  // onSave/saveVisualPage flow as the manual Save button (which is unchanged and
  // still available). A ref always points at the latest handleSave so the timer
  // saves the current tree without a stale closure, and without re-running this
  // effect on unrelated re-renders (selection/hover) that would keep resetting
  // the debounce. Undo/Redo simply produce a new tree, so the reverted state is
  // auto-saved too — the history stack itself is untouched.
  const latestSaveRef = useRef(handleSave);
  latestSaveRef.current = handleSave;
  useEffect(() => {
    if (readOnly || !onSave) return undefined;
    if (tree === autosaveSeedRef.current) return undefined; // no user change yet
    const id = setTimeout(() => { if (latestSaveRef.current) latestSaveRef.current(); }, AUTOSAVE_MS);
    return () => clearTimeout(id);
  }, [tree, readOnly, onSave]);

  // Save the current tree into a brand-new visual_pages row. Caller decides
  // how to prompt for title/slug (Page.js uses window.prompt for now); the
  // callback receives the current tree and is expected to POST it via
  // pages/createvisualpage and navigate to the new page's /edit route.
  const handleSaveAsNew = async () => {
    if (!onSaveAsNew) return;
    try {
      setSaveState({ status: 'saving', message: 'Saving as new…' });
      const result = await onSaveAsNew(tree);
      // A false / null result means the caller cancelled (e.g. user hit
      // Cancel on the title prompt) — reset messaging silently.
      if (result === false || result === null) {
        setSaveState({ status: 'idle', message: '' });
        return;
      }
      setSaveState({ status: 'saved', message: 'Saved as new page' });
      setTimeout(() => setSaveState((s) => (s.status === 'saved' ? { status: 'idle', message: '' } : s)), 2000);
    } catch (err) {
      setSaveState({ status: 'error', message: 'Save-as-new failed: ' + (err && err.message ? err.message : 'unknown') });
    }
  };

  // Publish / Unpublish. Saves the current tree AND flips the page's public
  // visibility in one backend call (pages/savevisualpage with an explicit
  // `status`): 1 = published (publicly viewable at its URL, guests included),
  // 0 = unpublished (hidden from the public; still editable by admins). The
  // callback is only supplied to the admin editor, so this control never
  // appears in the read-only public view.
  const handlePublish = async () => {
    if (!onPublish) return;
    const target = isPublished ? 0 : 1;
    try {
      setSaveState({ status: 'saving', message: target ? 'Publishing…' : 'Unpublishing…' });
      const result = await onPublish(tree, target);
      if (result === false || result === null) {
        setSaveState({ status: 'idle', message: '' });
        return;
      }
      setIsPublished(!!target);
      setSaveState({
        status: 'saved',
        message: target ? 'Published — now publicly viewable at its URL' : 'Unpublished — hidden from the public',
      });
      setTimeout(() => setSaveState((s) => (s.status === 'saved' ? { status: 'idle', message: '' } : s)), 2500);
    } catch (err) {
      setSaveState({ status: 'error', message: 'Publish failed: ' + (err && err.message ? err.message : 'unknown') });
    }
  };

  // Read-only render path: reuse the same Container/NodeView chain (which itself
  // reuses the T[type].render/style logic) but with ctx.readOnly = true so all
  // drag/click/toolbar/drop-zone chrome is stripped. No sidebar, inspector, or topbar.
  if (readOnly) {
    return (
      <div
        className="page-editor-root"
        style={{ display: 'block', height: 'auto', minHeight: 0, background: 'transparent' }}
      >
        <div className="pe-canvas-wrap" style={{ overflow: 'visible', padding: 0, background: 'transparent' }}>
          <div className="pe-canvas">
            {tree.children && tree.children.length > 0 ? (
              <Container node={tree} path={[]} ctx={ctx} />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-editor-root" onDragEnd={onDragEnd}>
      <div className="pe-topbar">
        <h1>Visual Page Builder</h1>
        <button className="ghost pe-undo" onClick={undo} disabled={!canUndo}
                title="Undo (Ctrl+Z)">↶ Undo</button>
        <button className="ghost pe-redo" onClick={redo} disabled={!canRedo}
                title="Redo (Ctrl+Y / Ctrl+Shift+Z)">↷ Redo</button>
        <div className="pe-spacer" />
        {onSave && saveState.message ? (
          <span
            style={{
              fontSize: 12,
              opacity: 0.85,
              color: saveState.status === 'error' ? '#fca5a5' : '#c5cbd6',
              marginRight: 6,
            }}
          >
            {saveState.message}
          </span>
        ) : null}
        {onSave ? (
          <button onClick={handleSave} disabled={saveState.status === 'saving'}
                  title="Overwrite this visual page with the current tree.">
            {saveState.status === 'saving' ? 'Saving…' : 'Save Changes to Existing Page'}
          </button>
        ) : null}
        {onPublish ? (
          <button
            onClick={handlePublish}
            disabled={saveState.status === 'saving'}
            style={{
              background: isPublished ? '#374151' : '#15803d',
              color: '#fff', fontWeight: 600,
            }}
            title={isPublished
              ? 'Unpublish — hide this page from the public. Admins can still edit it.'
              : 'Publish — save and make this page publicly viewable at its URL (guests included).'}
          >
            {isPublished ? '● Published — Unpublish' : 'Publish'}
          </button>
        ) : null}
        {onSaveAsNew ? (
          <button className="secondary" onClick={handleSaveAsNew}
                  disabled={saveState.status === 'saving'}
                  title="Clone the current tree into a brand-new visual page. This page is not modified.">
            Save as New Visual Page
          </button>
        ) : null}
        <button className="ghost" onClick={resetToSample}>Load sample</button>
        <button className="secondary" onClick={clearAll}>Clear</button>
        <button className="secondary" onClick={() => setShowImport(true)}>Import JSON</button>
        <button onClick={() => setShowExport(true)}>Export JSON</button>
      </div>

      <Breadcrumb tree={tree} selectedPath={selectedPath} onSelect={setSelectedId} />

      <div className="pe-sidebar">
        <h2>Containers</h2>
        {filterPalette('containers', CONTAINER_TYPES, pageType).map(t => (
          <PaletteItem key={t} type={t} onDragStart={startPaletteDrag} container />
        ))}
        <h2>Elements</h2>
        {filterPalette('elements', ELEMENT_TYPES, pageType).map(t => (
          <PaletteItem key={t} type={t} onDragStart={startPaletteDrag} />
        ))}
        <h2>Dynamic Components</h2>
        {filterPalette('dynamic', DYNAMIC_TYPES, pageType, tree.props && tree.props.sourceBlocks).flatMap(t => {
          const variants = T[t] && T[t].paletteVariants;
          // A type that declares palette variants (e.g. dynamic-cta -> Apply /
          // Enroll) renders one draggable item per variant; every other type
          // renders its single default item.
          if (variants && variants.length) {
            return variants.map(v => (
              <PaletteItem key={t + ':' + v.key} type={t} label={v.label} icon={v.icon}
                           preset={v.props} onDragStart={startPaletteDrag} dynamic />
            ));
          }
          return [<PaletteItem key={t} type={t} onDragStart={startPaletteDrag} dynamic />];
        })}
        <div style={{fontSize:11,color:"#6b7280",marginTop:14,padding:"0 4px"}}>
          {pageType
            ? `Palette limited to items available for “${pageType}” pages. `
            : ''}
          Drag onto the canvas. Dynamic components fetch live data from the Yii2
          backend when rendered — the tree only stores their type and config.
        </div>
      </div>

      <div className="pe-canvas-wrap" onClick={() => setSelectedId(null)}>
        <div className={"pe-canvas" + (tree.children.length === 0 ? " empty" : "")}>
          {tree.children.length === 0 ? (
            <EmptySlot parentPath={[]} idx={0} ctx={ctx} label="Drag a Section here to begin" />
          ) : (
            <Container node={tree} path={[]} ctx={ctx} />
          )}
        </div>
      </div>

      <div className="pe-inspector">
        <h2>Inspector</h2>
        {selectedNode ? (
          <Inspector node={selectedNode} path={selectedPath}
                     isAdministrator={!!isAdministrator}
                     onChange={(patch) => updateProps(selectedPath, patch)}
                     onDelete={() => removeNode(selectedPath)}
                     onDuplicate={() => duplicateNode(selectedPath)}
                     onMove={(d) => moveNodeBy(selectedPath, d)} />
        ) : (
          <div className="empty-state">Select any element on the canvas to edit its properties.</div>
        )}
      </div>

      {showExport && <ExportModal json={exportJson()} onClose={() => setShowExport(false)} onDownload={downloadJson} />}
      {showImport && <ImportModal value={importText} onChange={setImportText} onClose={() => setShowImport(false)} onImport={doImport} />}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================
function PaletteItem({ type, onDragStart, container, dynamic, label, icon, preset }) {
  const def = T[type];
  const cls = ["pe-palette-item"];
  if (container) cls.push("container-item");
  if (dynamic)   cls.push("dynamic-item");
  // label/icon fall back to the type's registry values; a palette variant
  // (e.g. "Enroll Button") overrides them and carries a `preset` prop set.
  const displayLabel = label || def.label;
  const displayIcon  = icon  || def.icon;
  return (
    <div className={cls.join(" ")}
         draggable onDragStart={(e) => onDragStart(e, type, preset)}
         title={"Drag onto canvas: " + displayLabel}>
      <span className="icon">{displayIcon}</span>
      <span>{displayLabel}</span>
    </div>
  );
}

function Breadcrumb({ tree, selectedPath, onSelect }) {
  if (!selectedPath) return <div className="pe-breadcrumb"><span className="crumb active">Page</span></div>;
  const crumbs = [{ id: tree.id, label: "Page" }];
  let n = tree;
  for (const i of selectedPath) {
    n = n.children[i];
    crumbs.push({ id: n.id, label: (T[n.type] && T[n.type].label) || n.type });
  }
  return (
    <div className="pe-breadcrumb">
      {crumbs.map((c, i) => (
        <Fragment key={c.id}>
          <span className={"crumb" + (i === crumbs.length - 1 ? " active" : "")}
                onClick={() => onSelect(i === 0 ? null : c.id)}>{c.label}</span>
          {i < crumbs.length - 1 ? <span className="sep">›</span> : null}
        </Fragment>
      ))}
    </div>
  );
}

function DropZone({ parentPath, idx, horizontal, ctx }) {
  const isActive = ctx.dragOver && pathsEqual(ctx.dragOver.parentPath, parentPath) && ctx.dragOver.idx === idx;
  return (
    <div className={"pe-drop-zone" + (horizontal ? " horizontal" : "") + (isActive ? " active" : "")}
         onDragOver={(e) => ctx.onDropZoneOver(e, parentPath, idx)}
         onDrop={(e) => ctx.onDropZoneDrop(e, parentPath, idx)} />
  );
}

function EmptySlot({ parentPath, idx, ctx, label }) {
  const isActive = ctx.dragOver && pathsEqual(ctx.dragOver.parentPath, parentPath) && ctx.dragOver.idx === idx;
  return (
    <div className={"pe-empty-slot" + (isActive ? " active" : "")}
         onDragOver={(e) => ctx.onDropZoneOver(e, parentPath, idx)}
         onDrop={(e) => ctx.onDropZoneDrop(e, parentPath, idx)}>
      {label || "Drop here"}
    </div>
  );
}

function NodeToolbar({ path, def, ctx, isRoot }) {
  return (
    <div className="pe-node-toolbar" onClick={(e) => e.stopPropagation()}>
      {/* Dedicated drag handle: initiates THIS node's drag by its path. A
          section's body is filled with its own draggable children, which would
          otherwise capture the drag (innermost draggable wins), so grabbing
          the handle is what lets a whole section/container be picked up and
          dropped at any position. Reuses the existing move/drop machinery. */}
      {!isRoot && (
        <span
          className="pe-drag-handle"
          title="Drag to move to any position"
          draggable
          onDragStart={(e) => ctx.startNodeDrag(e, path)}
        >
          ⠿
        </span>
      )}
      <span className="label">{def.label}</span>
      {!isRoot && (
        <>
          <button title="Move up" onClick={() => ctx.moveNodeBy(path, -1)}>↑</button>
          <button title="Move down" onClick={() => ctx.moveNodeBy(path, +1)}>↓</button>
          <button title="Duplicate" onClick={() => ctx.duplicateNode(path)}>⧉</button>
          <button title="Delete" onClick={() => ctx.removeNode(path)}>✕</button>
        </>
      )}
    </div>
  );
}

function Container({ node, path, ctx }) {
  const isRoot = node.type === "root";
  const def = T[node.type] || { label: "Page", layout: "vertical", className: "" };
  const horizontal = def.layout === "horizontal";
  const readOnly = !!ctx.readOnly;
  const selected = !readOnly && ctx.selectedId === node.id;
  const hovered = !readOnly && ctx.hoveredId === node.id && !selected;
  const nodeRef = useRef(null);
  const resizeSpec = !readOnly && !isRoot ? getResizeSpec(node.type) : null;

  const children = node.children || [];
  const innerStyle = def.innerStyle ? def.innerStyle(node.props) : null;
  const containerStyle = def.style ? def.style(node.props) : {};

  const innerContent = (
    <div className={"pe-container-inner" + (horizontal ? " row-inner" : "")}
         style={horizontal ? {
           display: "flex",
           alignItems: node.props.align || "stretch",
           justifyContent: node.props.justify || "flex-start",
           gap: (Number(node.props.gap)||0)+"px",
           flexWrap: node.props.wrap || "nowrap",
           flexDirection: node.props.reverse ? "row-reverse" : "row",
         } : (node.type === "column" ? {
           display: "flex", flexDirection: "column",
           alignItems: node.props.alignItems || "stretch",
           justifyContent: node.props.justifyContent || "flex-start",
         } : null)}>
      {children.length === 0 ? (
        readOnly ? null : (
          <EmptySlot parentPath={path} idx={0} ctx={ctx}
                     label={"Drop " + (node.type === "row" ? "columns" : "elements") + " here"} />
        )
      ) : (
        <>
          {readOnly ? null : <DropZone parentPath={path} idx={0} horizontal={horizontal} ctx={ctx} />}
          {children.map((child, i) => (
            <Fragment key={child.id}>
              <NodeView node={child} path={path.concat(i)} ctx={ctx} />
              {readOnly ? null : <DropZone parentPath={path} idx={i+1} horizontal={horizontal} ctx={ctx} />}
            </Fragment>
          ))}
        </>
      )}
    </div>
  );

  if (isRoot) {
    return <div className="pe-node root-node">{innerContent}</div>;
  }

  if (readOnly) {
    const roClasses = [ "pe-node", def.className || "" ].filter(Boolean).join(" ");
    return (
      <div className={roClasses} style={containerStyle}>
        {innerStyle ? <div style={innerStyle}>{innerContent}</div> : innerContent}
      </div>
    );
  }

  const classes = [ "pe-node", def.className || "", selected ? "selected" : "", hovered ? "hovered" : "" ].filter(Boolean).join(" ");

  const onResizeDelta = resizeSpec
    ? (handle, dx, dy, initial) => {
        const patch = computeResizePatch(
          node, handle, dx, dy,
          { ...initial, startProps: { ...node.props } }
        );
        if (patch) ctx.updateProps(path, patch);
      }
    : null;

  return (
    <div ref={nodeRef} className={classes}
         style={containerStyle}
         draggable
         onDragStart={(e) => ctx.startNodeDrag(e, path)}
         onClick={(e) => { e.stopPropagation(); ctx.setSelectedId(node.id); }}
         onMouseOver={(e) => { e.stopPropagation(); ctx.setHoveredId(node.id); }}
         onMouseOut={(e) => { e.stopPropagation(); ctx.setHoveredId(null); }}>
      <NodeToolbar path={path} def={def} ctx={ctx} />
      {innerStyle ? <div style={innerStyle}>{innerContent}</div> : innerContent}
      {resizeSpec && selected ? (
        <ResizeHandles
          handles={resizeSpec.handles}
          nodeRef={nodeRef}
          targetSelector={resizeSpec.target}
          onDelta={onResizeDelta}
        />
      ) : null}
    </div>
  );
}

function NodeView({ node, path, ctx }) {
  const def = T[node.type];
  const nodeRef = useRef(null);
  if (def.container) return <Container node={node} path={path} ctx={ctx} />;
  const readOnly = !!ctx.readOnly;
  if (readOnly) {
    return <div className="pe-node">{def.render(node.props, { contextPage: ctx.contextPage, sourcePath: ctx.sourcePath, readOnly: true })}</div>;
  }
  const selected = ctx.selectedId === node.id;
  const hovered = ctx.hoveredId === node.id && !selected;
  const classes = [ "pe-node", selected ? "selected" : "", hovered ? "hovered" : "" ].filter(Boolean).join(" ");

  const resizeSpec = getResizeSpec(node.type);
  const onResizeDelta = resizeSpec
    ? (handle, dx, dy, initial) => {
        const patch = computeResizePatch(
          node, handle, dx, dy,
          { ...initial, startProps: { ...node.props } }
        );
        if (patch) ctx.updateProps(path, patch);
      }
    : null;
  // Handles slot passed into render — image / video render functions inject
  // them inside their visible frame so grabbers hug the actual media instead
  // of the full-width .pe-node wrapper.
  const handlesSlot = resizeSpec && selected
    ? <ResizeHandles
        handles={resizeSpec.handles}
        nodeRef={nodeRef}
        targetSelector={resizeSpec.target}
        onDelta={onResizeDelta} />
    : null;

  return (
    <div ref={nodeRef} className={classes}
         draggable
         onDragStart={(e) => ctx.startNodeDrag(e, path)}
         onClick={(e) => { e.stopPropagation(); ctx.setSelectedId(node.id); }}
         onMouseOver={(e) => { e.stopPropagation(); ctx.setHoveredId(node.id); }}
         onMouseOut={(e) => { e.stopPropagation(); ctx.setHoveredId(null); }}
         style={{ padding: "6px 4px" }}>
      <NodeToolbar path={path} def={def} ctx={ctx} />
      {def.render(node.props, { handles: handlesSlot, contextPage: ctx.contextPage, sourcePath: ctx.sourcePath, readOnly: false })}
    </div>
  );
}

function Inspector({ node, path, onChange, onDelete, onDuplicate, onMove, isAdministrator }) {
  const def = T[node.type];
  const rows = [];
  let lastGroup = null;
  // Fields flagged `adminOnly` (e.g. the CTA Action + Button text controls) are
  // only offered to the Administrator role. SAAdmin can still open the editor
  // and edit everything else, but never sees these controls.
  const fields = def.fields.filter((f) => !f.adminOnly || isAdministrator);
  for (const f of fields) {
    const g = f.group || null;
    if (g !== lastGroup) {
      if (g) rows.push(<div key={"g_" + f.name} className="pe-group-label">{g}</div>);
      lastGroup = g;
    }
    rows.push(
      <Field key={f.name} field={f}
             value={node.props[f.name]}
             onChange={(v) => onChange({ [f.name]: v })} />
    );
  }
  return (
    <div>
      <div className="node-title">{def.label}</div>
      <div className="node-id">id: {node.id} · path: [{path.join(", ")}]</div>
      <div className="pe-inspector-actions">
        <button onClick={() => onMove(-1)}>↑ Up</button>
        <button onClick={() => onMove(+1)}>↓ Down</button>
        <button onClick={onDuplicate}>⧉ Duplicate</button>
        <button className="danger" onClick={onDelete}>✕ Delete</button>
      </div>
      {fields.length === 0 ? (
        <div className="empty-state">No editable properties.</div>
      ) : rows}
    </div>
  );
}

// ============================================================
// Direct-manipulation resize handles
// ============================================================

// Which handles to render per type, and — for elements whose visible bounds
// differ from the .pe-node wrapper — the CSS selector we should measure to
// figure the initial pixel size from. Containers use the .pe-node itself.
// All resizable types get the full 8-handle set (N/S/E/W + 4 corners) for
// familiarity; each type's computeResizePatch decides which axes are live.
const ALL_HANDLES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
function getResizeSpec(type) {
  switch (type) {
    case "image":   return { handles: ALL_HANDLES, target: "img, .l-image-empty",  axes: "wh" };
    case "video":   return { handles: ALL_HANDLES, target: ".l-video, .l-video-empty", axes: "wh" };
    case "card":    return { handles: ALL_HANDLES, target: null, axes: "wh" };
    case "section": return { handles: ALL_HANDLES, target: null, axes: "h" };
    case "row":     return { handles: ALL_HANDLES, target: null, axes: "h" };
    case "column":  return { handles: ALL_HANDLES, target: null, axes: "wh-flex" };
    default:        return null;
  }
}

// Translate a raw pixel delta from a drag into the actual prop patch this
// node type expects. Returning null means "no change for this frame".
//
// Handle naming: any handle with "n"/"s" affects the vertical axis; any with
// "e"/"w" affects horizontal. Dragging the west edge to the left grows the
// element, so we use `-dx` (and mirror for north).
function computeResizePatch(node, handle, dx, dy, initial) {
  const t = node.type;
  const west  = handle.includes("w");
  const east  = handle.includes("e");
  const north = handle.includes("n");
  const south = handle.includes("s");

  if (t === "image" || t === "video" || t === "card") {
    const patch = {};
    if (east)       patch.width  = Math.max(20, Math.round(initial.pxW + dx)) + "px";
    else if (west)  patch.width  = Math.max(20, Math.round(initial.pxW - dx)) + "px";
    if (south)      patch.height = Math.max(20, Math.round(initial.pxH + dy)) + "px";
    else if (north) patch.height = Math.max(20, Math.round(initial.pxH - dy)) + "px";
    return Object.keys(patch).length ? patch : null;
  }
  if (t === "section" || t === "row") {
    // Full-width in flow — only the vertical axis is meaningful; W/E on their
    // own are no-ops. Corner handles still bite on the vertical component.
    if (south)      return { minHeight: Math.max(0, Math.round(initial.pxH + dy)) };
    if (north)      return { minHeight: Math.max(0, Math.round(initial.pxH - dy)) };
    return null;
  }
  if (t === "column") {
    // Width is controlled by flex weight; we rewrite it proportionally to the
    // measured pixel change so siblings absorb the difference naturally.
    const patch = {};
    if (initial.pxW > 0) {
      const startWeight = Number(initial.startProps.weight) || 1;
      if (east) {
        const newW = Math.max(20, initial.pxW + dx);
        patch.weight = Math.round((startWeight * newW) / initial.pxW * 100) / 100;
      } else if (west) {
        const newW = Math.max(20, initial.pxW - dx);
        patch.weight = Math.round((startWeight * newW) / initial.pxW * 100) / 100;
      }
    }
    if (south)      patch.minHeight = Math.max(0, Math.round(initial.pxH + dy));
    else if (north) patch.minHeight = Math.max(0, Math.round(initial.pxH - dy));
    return Object.keys(patch).length ? patch : null;
  }
  return null;
}

// Render a small set of edge / corner grabbers. mousedown starts a document-
// level pointermove capture so the drag survives the cursor leaving the node.
// preventDefault is important — it stops the ancestor's HTML5 drag-to-move
// from kicking in when the user is trying to resize.
function ResizeHandles({ handles, nodeRef, targetSelector, onDelta }) {
  const start = (handle) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const nodeEl = nodeRef.current;
    if (!nodeEl) return;
    const target = targetSelector ? nodeEl.querySelector(targetSelector) : nodeEl;
    const rect = (target || nodeEl).getBoundingClientRect();
    const initial = { pxW: rect.width, pxH: rect.height };
    const startX = e.clientX;
    const startY = e.clientY;
    const move = (ev) => onDelta(handle, ev.clientX - startX, ev.clientY - startY, initial);
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return (
    <>
      {handles.map((h) => (
        <div key={h}
             className={"pe-resize-handle h-" + h}
             onMouseDown={start(h)}
             onClick={(e) => e.stopPropagation()}
             onDragStart={(e) => e.preventDefault()} />
      ))}
    </>
  );
}

function Field({ field, value, onChange }) {
  const v = value ?? "";
  let control;
  if (field.type === "textarea") {
    control = <textarea value={v} onChange={(e) => onChange(e.target.value)} />;
  } else if (field.type === "select") {
    control = <select value={v} onChange={(e) => onChange(e.target.value)}>
      {field.options.map(o => (
        <option key={o} value={o}>{o === "" ? "(default)" : o}</option>
      ))}
    </select>;
  } else if (field.type === "number") {
    control = <input type="number" value={v} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  } else if (field.type === "color") {
    return (
      <div className="pe-field">
        <label>{field.label}</label>
        <div className="pe-field-row">
          <div className="pe-field"><input type="color" value={/^#/.test(v) ? v : "#ffffff"} onChange={(e) => onChange(e.target.value)} /></div>
          <div className="pe-field"><input type="text" value={v} onChange={(e) => onChange(e.target.value)} placeholder="#rrggbb or empty" /></div>
        </div>
        {field.hint ? <div className="hint">{field.hint}</div> : null}
      </div>
    );
  } else if (field.type === "checkbox") {
    return (
      <div className="pe-field">
        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {field.label}
        </label>
        {field.hint ? <div className="hint">{field.hint}</div> : null}
      </div>
    );
  } else if (field.type === "sides") {
    return <SidesField field={field} value={value} onChange={onChange} />;
  } else if (field.type === "file") {
    return <FileField field={field} value={v} onChange={onChange} />;
  } else {
    control = <input type={field.type === "url" ? "url" : "text"} value={v} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className="pe-field">
      <label>{field.label}</label>
      {control}
      {field.hint ? <div className="hint">{field.hint}</div> : null}
    </div>
  );
}

// Four mini number inputs (Top / Right / Bottom / Left) that read a numeric
// legacy value (padding: 60) as "all sides" and write back a {t,r,b,l} object.
function SidesField({ field, value, onChange }) {
  const cur = normalizeSides(value);
  const set = (side, n) => onChange({ ...cur, [side]: n });
  const linked = value == null || typeof value === "number" || typeof value === "string";
  const [link, setLink] = useState(linked);
  const setAll = (n) => onChange({ t: n, r: n, b: n, l: n });
  return (
    <div className="pe-field">
      <label>
        {field.label}
        <button type="button" className="pe-side-link"
                onClick={() => setLink((x) => !x)}
                title={link ? "Unlink sides" : "Link all sides"}>
          {link ? "🔗" : "⛓️‍💥"}
        </button>
      </label>
      {link ? (
        <input type="number" value={cur.t}
               onChange={(e) => setAll(e.target.value === "" ? 0 : Number(e.target.value))} />
      ) : (
        <div className="pe-sides">
          {[["t","T"],["r","R"],["b","B"],["l","L"]].map(([k, label]) => (
            <div key={k} className="pe-side">
              <input type="number" value={cur[k]}
                     onChange={(e) => set(k, e.target.value === "" ? 0 : Number(e.target.value))} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}
      {field.hint ? <div className="hint">{field.hint}</div> : null}
    </div>
  );
}

// URL input + "Upload" button. For local upload we store a data URL directly
// in the node's props — that's fine for a self-contained visual page and
// requires no backend upload endpoint.
function FileField({ field, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    try {
      setBusy(true);
      const url = await readFileAsDataURL(f);
      onChange(url);
    } catch (err) {
      window.alert("Upload failed: " + (err && err.message ? err.message : "unknown"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const short = value && value.length > 60
    ? value.slice(0, 40) + "…" + value.slice(-10)
    : value;
  const isData = typeof value === "string" && /^(data:|blob:)/.test(value);
  return (
    <div className="pe-field">
      <label>{field.label}</label>
      <div className="pe-file-row">
        {isData ? (
          <input type="text" value={short} readOnly title={value.slice(0, 200)} />
        ) : (
          <input type="url" value={value || ""} placeholder="Paste URL"
                 onChange={(e) => onChange(e.target.value)} />
        )}
        <label className={"pe-file-btn" + (busy ? " busy" : "")}>
          <input type="file" accept={field.accept || "*"} onChange={onPick}
                 style={{ display: "none" }} />
          {busy ? "Uploading…" : "Upload"}
        </label>
        {value ? (
          <button type="button" className="pe-file-clear"
                  onClick={() => onChange("")} title="Clear">✕</button>
        ) : null}
      </div>
      {field.hint ? <div className="hint">{field.hint}</div> : null}
    </div>
  );
}

function ExportModal({ json, onClose, onDownload }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(json); window.alert("Copied to clipboard."); }
    catch (e) { window.alert("Copy failed — select and copy manually."); }
  };
  return (
    <div className="pe-modal-backdrop" onClick={onClose}>
      <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Export page layout (JSON)</h3>
        <textarea readOnly value={json} onFocus={(e) => e.target.select()} />
        <div className="pe-modal-actions">
          <button className="secondary" onClick={onClose}>Close</button>
          <button className="secondary" onClick={copy}>Copy</button>
          <button className="primary" onClick={onDownload}>Download .json</button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ value, onChange, onClose, onImport }) {
  return (
    <div className="pe-modal-backdrop" onClick={onClose}>
      <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Import page layout (JSON)</h3>
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
                  placeholder='{ "version": 2, "tree": { "type": "root", "children": [...] } }' />
        <div className="pe-modal-actions">
          <button className="secondary" onClick={onClose}>Cancel</button>
          <button className="primary" onClick={onImport}>Import</button>
        </div>
      </div>
    </div>
  );
}

export default PageEditor;
